import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useLocation, useNavigate, Link } from 'react-router-dom';
import BottomSheet from './BottomSheet';
import { photoUrl } from './photoUtils';
import LoadingOverlay from './LoadingOverlay';
import Icon from './Icons';
import ErrorScreen from './ErrorScreen';
import { useGrowingMethods, GrowingMethodSubstrateSelect, formatSubstrate } from './GrowingMethodPicker';
import { fieldOptionList, useFieldOptions } from './fieldOptions';
import { SkeletonPlantDetail } from './Skeleton';
import HistorySuggestInput from './HistorySuggestInput';
import SpeciesCareProfile from './SpeciesCareProfile';
import { fetchCareProfile } from './api';
import { formatStatus, statusStyle, useStatusMeta } from './statuses';
import { useDateFormat } from './DateFormatContext';
import MarkAcquiredModal from './MarkAcquiredModal';
import StatusTransitionSheet from './StatusTransitionSheet';
import { plantDisplayName } from './nameUtils';
import IdentificationResults from './IdentificationResults';

// Event types and which ones have detail forms
const EVENT_TYPES = [
  { value: 'repot', label: 'Repot' },
  { value: 'fertilize', label: 'Fertilize' },
  { value: 'watering', label: 'Watering' },
  { value: 'treatment', label: 'Treatment' },
  { value: 'pruning', label: 'Pruning' },
  { value: 'propagation', label: 'Propagation' },
  { value: 'substrate_change', label: 'Substrate Change' },
  { value: 'acclimation', label: 'Acclimation' },
  { value: 'staking_training', label: 'Staking / Training' },
  { value: 'health_check', label: 'Health Check' },
  { value: 'general_note', label: 'General Note' },
];

const CARE_TASK_TYPES = [
  { value: 'health_check',     label: 'Health Check' },
  { value: 'watering',         label: 'Watering' },
  { value: 'fertilize',        label: 'Fertilize' },
  { value: 'repot',            label: 'Repot' },
  { value: 'treatment',        label: 'Treatment' },
  { value: 'pruning',          label: 'Pruning' },
  { value: 'staking_training', label: 'Staking / Training' },
  { value: 'substrate_change', label: 'Substrate Change' },
  { value: 'measurement',      label: 'Measurement' },
  { value: 'general_note',     label: 'General Note' },
];

const COLLAPSED_ACTIVITY_PREVIEW_COUNT = 3;

const PHOTO_PART_OPTIONS = [
  { value: '', label: 'Unlabeled' },
  { value: 'full_plant', label: 'Full plant' },
  { value: 'leaf', label: 'Leaf' },
  { value: 'petiole', label: 'Petiole' },
  { value: 'stem_node', label: 'Stem / node' },
  { value: 'roots', label: 'Roots' },
  { value: 'flower', label: 'Flower' },
  { value: 'fruit', label: 'Fruit' },
  { value: 'habit', label: 'Growth habit' },
  { value: 'other', label: 'Other' },
];

const PHOTO_PART_LABELS = Object.fromEntries(PHOTO_PART_OPTIONS.map(option => [option.value, option.label]));

function photoPartLabel(value) {
  return PHOTO_PART_LABELS[value] || null;
}

const PHOTO_GROWTH_STAGE_OPTIONS = [
  { value: '', label: 'Unspecified' },
  { value: 'new_growth', label: 'New growth' },
  { value: 'juvenile', label: 'Juvenile' },
  { value: 'transitional', label: 'Transitional' },
  { value: 'mature', label: 'Mature' },
];

const PHOTO_GROWTH_STAGE_LABELS = Object.fromEntries(PHOTO_GROWTH_STAGE_OPTIONS.map(option => [option.value, option.label]));

function photoGrowthStageLabel(value) {
  return PHOTO_GROWTH_STAGE_LABELS[value] || null;
}

function PhotoPartSelect({ value, onChange, className = 'form-input' }) {
  return (
    <select className={className} value={value || ''} onChange={e => onChange(e.target.value)}>
      {PHOTO_PART_OPTIONS.map(option => (
        <option key={option.value || 'none'} value={option.value}>{option.label}</option>
      ))}
    </select>
  );
}

function PhotoLabelField({ value, onChange, className = '' }) {
  return (
    <label className={`photo-label-field ${className}`}>
      <span>Photo label</span>
      <PhotoPartSelect value={value} onChange={onChange} />
    </label>
  );
}

function PhotoGrowthStageSelect({ value, onChange, className = 'form-input' }) {
  return (
    <select className={className} value={value || ''} onChange={e => onChange(e.target.value)}>
      {PHOTO_GROWTH_STAGE_OPTIONS.map(option => (
        <option key={option.value || 'none'} value={option.value}>{option.label}</option>
      ))}
    </select>
  );
}

function LightboxSelectField({ label, children }) {
  return (
    <label className="lightbox-select-field">
      <span>{label}</span>
      {children}
    </label>
  );
}

const PROPAGATION_METHOD_LABELS = {
  cutting: 'Cutting',
  wet_stick: 'Wet Stick',
  division: 'Division',
  offset_pup: 'Offset / Pup',
  corm: 'Corm',
  air_layer: 'Air Layer',
  tissue_culture: 'Tissue Culture',
  runner_stolon: 'Runner / Stolon',
  rhizome_division: 'Rhizome Division',
  bulbil: 'Bulbil',
  graft: 'Graft',
  other_clone: 'Other Clone Method',
  seed_self: 'Self-pollinated',
  seed_cross: 'Cross-pollinated',
  seed_open: 'Open-pollinated',
  seed_unknown: 'Unknown pollen donor',
};

const CLONE_METHODS = [
  ['cutting', 'Cutting'],
  ['wet_stick', 'Wet Stick'],
  ['division', 'Division'],
  ['offset_pup', 'Offset / Pup'],
  ['corm', 'Corm'],
  ['air_layer', 'Air Layer'],
  ['tissue_culture', 'Tissue Culture'],
  ['runner_stolon', 'Runner / Stolon'],
  ['rhizome_division', 'Rhizome Division'],
  ['bulbil', 'Bulbil'],
  ['graft', 'Graft'],
  ['other_clone', 'Other Clone Method'],
];

const WOUND_TREATMENT_METHODS = new Set(['cutting', 'wet_stick', 'rhizome_division', 'division', 'other_clone']);

const POLLINATION_TYPES = [
  ['self', 'Self-pollinated'],
  ['cross', 'Cross-pollinated (Hybrid)'],
  ['open', 'Open-pollinated'],
  ['unknown', 'Unknown pollen donor'],
];

function methodForPollinationType(type) {
  if (type === 'cross') return 'seed_cross';
  if (type === 'open') return 'seed_open';
  if (type === 'unknown') return 'seed_unknown';
  return 'seed_self';
}

function categoryFromPropagation(propagation) {
  if (propagation?.reproduction_category) return propagation.reproduction_category;
  if (['seed_self', 'seed_cross', 'seed_open', 'seed_unknown'].includes(propagation?.method)) return 'seed';
  return 'clone';
}

function pollinationFromPropagation(propagation) {
  if (propagation?.pollination_type) return propagation.pollination_type;
  if (propagation?.method === 'seed_cross') return 'cross';
  if (propagation?.method === 'seed_open') return 'open';
  if (propagation?.method === 'seed_unknown') return 'unknown';
  return 'self';
}

// Recursive lineage tree node
function TreeNode({ node, depth = 0 }) {
  const indent = depth * 20;
  const { formatDate } = useDateFormat();

  return (
    <div style={{ marginLeft: indent, marginTop: depth === 0 ? 0 : '0.5rem' }}>
      <div className={`lineage-node${node.is_focus ? ' lineage-node--focus' : ''}`}>
        {depth > 0 && <span className="lineage-node-indent">↳</span>}
        <Link
          to={`/plants/${node.id}`}
          style={{ fontWeight: node.is_focus ? 700 : 400, fontSize: '0.9rem', color: 'var(--green)' }}
        >
          {node.specimen_id}
        </Link>
        <span className="lineage-node-name">{node.genus} {node.species}</span>
        {node.common_name && (
          <span className="lineage-node-common">— {node.common_name}</span>
        )}
        <span className={`status-badge status-${node.status}`} style={{ marginLeft: 'auto', fontSize: '0.72rem' }}>
          {node.status}
        </span>
        {node.propagation_method && (
          <span className="lineage-node-method">
            propagated from {(PROPAGATION_METHOD_LABELS[node.propagation_method] || node.propagation_method).toLowerCase()}
            {node.propagation_date ? ` · ${formatDate(node.propagation_date)}` : ''}
          </span>
        )}
        {node.is_focus && (
          <span className="lineage-node-current">
            <Icon name="arrowLeft" size={11} />current
          </span>
        )}
      </div>
      {node.children && node.children.map(child => (
        <TreeNode key={child.id} node={child} depth={depth + 1} />
      ))}
    </div>
  );
}

const WISHLIST_PRIORITY_LABELS = {
  1: 'Top priority',
  2: 'High priority',
  3: 'Medium priority',
  4: 'Low priority',
  5: 'Backburner',
};

const INTERNAL_PLANT_HISTORY_FIELDS = new Set([
  'acquisition_disposition_id',
  'source_propagation_id',
]);

function formatWishlistPriority(value) {
  if (value == null || value === '') return 'Unspecified';
  return WISHLIST_PRIORITY_LABELS[Number(value)] || `Priority ${value}`;
}

function formatDispositionType(type) {
  return {
    sold: 'Sold',
    gifted: 'Gifted',
    swapped: 'Swapped',
    deceased: 'Deceased',
    discarded: 'Discarded',
    other: 'Archived',
  }[type] || (type || '').replaceAll('_', ' ');
}

function formatMoney(value) {
  return value == null || value === '' ? null : `$${Number(value).toFixed(2)}`;
}

function parseLoggedDate(value) {
  if (!value) return null;
  return new Date(value.includes('T') ? value : `${value.replace(' ', 'T')}Z`);
}

function formatLoggedTime(value) {
  const date = parseLoggedDate(value);
  if (!date || Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatLocalDate(date) {
  const copy = new Date(date);
  copy.setMinutes(copy.getMinutes() - copy.getTimezoneOffset());
  return copy.toISOString().split('T')[0];
}

function addDays(days) {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return formatLocalDate(date);
}

function nextSaturday() {
  const date = new Date();
  const day = date.getDay();
  const daysUntilSaturday = (6 - day + 7) % 7 || 7;
  date.setDate(date.getDate() + daysUntilSaturday);
  return formatLocalDate(date);
}

function PlantCareScheduleModal({ plant, onSaved, onCancel }) {
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState({
    task_type: 'health_check',
    scheduled_date: nextSaturday(),
    assigned_to_user_id: '',
    notes: '',
    recurrence_type: 'once',
    interval_days: 7,
    total_cycles: 3,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch('/api/plan/household-members')
      .then(r => r.ok ? r.json() : [])
      .then(setMembers)
      .catch(() => setMembers([]));
  }, []);

  function update(key, value) {
    setForm(prev => ({ ...prev, [key]: value }));
  }

  function handleSave() {
    setSaving(true);
    setError(null);
    const body = {
      plant_id: plant.id,
      task_type: form.task_type,
      starts_on: form.scheduled_date,
      assigned_to_user_id: form.assigned_to_user_id ? parseInt(form.assigned_to_user_id) : null,
      notes: form.notes || null,
      recurrence_type: form.recurrence_type,
      interval_days: form.recurrence_type !== 'once' ? form.interval_days : null,
      total_cycles: form.recurrence_type === 'cycles' ? form.total_cycles : null,
    };

    fetch('/api/plan/schedules', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then(r => { if (!r.ok) return r.json().then(e => { throw new Error(e.detail || 'Failed to schedule care'); }); return r.json(); })
      .then(data => { setSaving(false); onSaved(data); })
      .catch(err => { setSaving(false); setError(err.message); });
  }

  return createPortal(
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-card plant-care-schedule-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h2>Schedule Care</h2>
            <p className="modal-subtitle">{plantDisplayName(plant)} · {plant.specimen_id}</p>
          </div>
          <button className="btn-icon" onClick={onCancel} aria-label="Close" title="Close">
            <Icon name="x" size={18} />
          </button>
        </div>

        <div className="form-grid plant-care-schedule-grid">
          <div className="form-field">
            <label className="form-label">Task type</label>
            <select className="form-input" value={form.task_type} onChange={e => update('task_type', e.target.value)}>
              {CARE_TASK_TYPES.map(type => <option key={type.value} value={type.value}>{type.label}</option>)}
            </select>
          </div>
          <div className="form-field">
            <label className="form-label">Date</label>
            <input type="date" className="form-input" value={form.scheduled_date} onChange={e => update('scheduled_date', e.target.value)} />
          </div>
        </div>

        <div className="quick-date-row">
          <button type="button" className="quick-date-chip" onClick={() => update('scheduled_date', addDays(1))}>Tomorrow</button>
          <button type="button" className="quick-date-chip" onClick={() => update('scheduled_date', nextSaturday())}>Weekend</button>
          <button type="button" className="quick-date-chip" onClick={() => update('scheduled_date', addDays(7))}>Next week</button>
        </div>

        {members.length > 1 && (
          <div className="form-field">
            <label className="form-label">Assign to</label>
            <select className="form-input" value={form.assigned_to_user_id} onChange={e => update('assigned_to_user_id', e.target.value)}>
              <option value="">No one assigned</option>
              {members.map(member => (
                <option key={member.id} value={member.id}>{member.display_name || member.email}</option>
              ))}
            </select>
          </div>
        )}

        <div className="form-field">
          <label className="form-label">Notes</label>
          <textarea className="form-input form-textarea" rows={2} value={form.notes} onChange={e => update('notes', e.target.value)} />
        </div>

        <div className="form-field">
          <label className="form-label">Repeat</label>
          <select className="form-input" value={form.recurrence_type} onChange={e => update('recurrence_type', e.target.value)}>
            <option value="once">Once</option>
            <option value="interval">Every N days</option>
            <option value="cycles">Every N days x M cycles</option>
          </select>
        </div>

        {form.recurrence_type !== 'once' && (
          <div className="form-grid plant-care-schedule-grid">
            <div className="form-field">
              <label className="form-label">Every days</label>
              <input type="number" min="1" max="365" className="form-input" value={form.interval_days} onChange={e => update('interval_days', parseInt(e.target.value) || 1)} />
            </div>
            {form.recurrence_type === 'cycles' && (
              <div className="form-field">
                <label className="form-label">Cycles</label>
                <input type="number" min="1" max="52" className="form-input" value={form.total_cycles} onChange={e => update('total_cycles', parseInt(e.target.value) || 1)} />
              </div>
            )}
          </div>
        )}

        {error && <p className="error">{error}</p>}

        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onCancel} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || !form.scheduled_date}>
            {saving ? 'Scheduling…' : 'Add Task'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function PlantDetail() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const growingMethods = useGrowingMethods();
  const fieldOptions = useFieldOptions();
  const { statuses: statusMetadata, byKey: statusByKey } = useStatusMeta();
  const { formatDate, formatDateTime } = useDateFormat();
  const [toast, setToast] = useState(null);
  const [plant, setPlant] = useState(null);
  const [observations, setObservations] = useState([]);
  const [measurements, setMeasurements] = useState([]);
  const [history, setHistory] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [dispositions, setDispositions] = useState([]);
  const [loans, setLoans] = useState([]);
  const [swapLinkWarning, setSwapLinkWarning] = useState(null);
  const [editingDispositionId, setEditingDispositionId] = useState(null);
  const [dispositionEditForm, setDispositionEditForm] = useState({});
  const [dispositionEditSaving, setDispositionEditSaving] = useState(false);
  const [dispositionEditError, setDispositionEditError] = useState('');
  const [dispositionSwapSearch, setDispositionSwapSearch] = useState('');
  const [dispositionSwapMatches, setDispositionSwapMatches] = useState([]);
  const [returningLoanId, setReturningLoanId] = useState(null);
  const [expandedReturnedLoans, setExpandedReturnedLoans] = useState(new Set());
  const [loanReturnForm, setLoanReturnForm] = useState({});
  const [loanReturnSaving, setLoanReturnSaving] = useState(false);
  const [loanReturnError, setLoanReturnError] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [observationsOpen, setObservationsOpen] = useState(false);
  const [measurementsOpen, setMeasurementsOpen] = useState(false);
  const [obsHistories, setObsHistories] = useState({});
  const [measHistories, setMeasHistories] = useState({});
  const [lineageOpen, setLineageOpen] = useState(false);
  const [lineage, setLineage] = useState(null);
  const [lineageLoading, setLineageLoading] = useState(false);
  const [lineageError, setLineageError] = useState(null);
  const [photosOpen, setPhotosOpen] = useState(true);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [uploading, setUploading] = useState(false);
  // Multi-photo identification state (replaces old single-photo PlantNet ID state)
  const [selectedPhotoIds, setSelectedPhotoIds] = useState(new Set());
  const [multiIdLoading, setMultiIdLoading] = useState(false);
  const [multiIdResults, setMultiIdResults] = useState(null);
  const [multiIdError, setMultiIdError] = useState(null);
  const [multiIdQuotaInfo, setMultiIdQuotaInfo] = useState(null);
  const [multiIdConfirm, setMultiIdConfirm] = useState(null); // { resultId, suggestion }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Care profile state
  const [careProfile, setCareProfile] = useState(undefined); // undefined = not yet fetched
  const [careProfileLoading, setCareProfileLoading] = useState(true);
  const [careGuideOpen, setCareGuideOpen] = useState(false);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);

  // Status pill
  const [statusPickerOpen, setStatusPickerOpen] = useState(false);
  const [statusChanging, setStatusChanging] = useState(false);
  const [acquireModalOpen, setAcquireModalOpen] = useState(false);
  const [transitionTarget, setTransitionTarget] = useState(null);

  // Confirmation sheet (Likely chip)
  const [confirmSheetOpen, setConfirmSheetOpen] = useState(false);
  const [idTraits, setIdTraits] = useState(null);
  const [idTraitsError, setIdTraitsError] = useState(false);
  const [traitsChecked, setTraitsChecked] = useState({});
  const [confirming, setConfirming] = useState(false);

  // Cover photo position mode
  const [positionMode, setPositionMode] = useState(false);
  const [draftOffset, setDraftOffset] = useState(null); // { x, y }
  const [draftZoom, setDraftZoom] = useState(null); // number >= 1
  const [savingPosition, setSavingPosition] = useState(false);
  const heroPanRef = useRef(null);
  const panStartRef = useRef(null); // { clientX, clientY, offsetX, offsetY }
  const obsFormRef  = useRef(null);
  const statusDetailsRef = useRef(null);
  const scrollToStatusAfterRefresh = useRef(false);
  const measFormRef = useRef(null);

  useEffect(() => {
    if (positionMode) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [positionMode]);

  // Inline confirmation state — replaces window.confirm everywhere
  // { type: 'obs'|'meas'|'photo'|'childCreate'|'parentEdit', id?, payload? }
  const [pendingConfirm, setPendingConfirm] = useState(null);
  const cancelConfirm = () => setPendingConfirm(null);

  // Observation form state
  const [obsFormOpen, setObsFormOpen] = useState(false);
  const [obsSaving, setObsSaving] = useState(false);
  const [obsError, setObsError] = useState(null);
  const [obsPhotoFile, setObsPhotoFile] = useState(null);
  const [obsPhotoPart, setObsPhotoPart] = useState('');
  const [obsForm, setObsForm] = useState({
    event_type: 'general_note',
    date: new Date().toISOString().split('T')[0],
    notes: '',
    // Repot fields
    new_pot_size: '',
    pot_type: '',
    new_growing_method_repot: '',
    new_substrate_repot: '',
    root_condition: '',
    // Fertilize fields
    fert_product: '',
    concentration: '',
    fert_method: '',
    // Treatment fields
    treatment_category: '',
    target: '',
    treat_product: '',
    treat_method: '',
    // Propagation fields
    prop_repro_type: 'clone',  // 'clone' | 'seed'
    prop_method: 'cutting',
    prop_pollination_type: 'self',
    prop_second_parent: null,    // full plant object for second parent (sexual/seed_cross)
    prop_mother_is_current: true, // when false, swap parent/second_parent ids
    prop_create_child: true,      // default on — opt-out rather than opt-in
    prop_batch_mode: false,       // true = skip single child, batch items added post-save
    prop_override_name: false,
    child_genus: '',
    child_species: '',
    child_common_name: '',
    child_status: 'propagation',
    prop_rooting_hormone: '',
    prop_hormone_concentration: '',
    prop_wound_treatment: [],
    // Substrate change fields
    old_growing_method: '',
    old_substrate: '',
    new_growing_method: '',
    new_substrate: '',
    change_reason: '',
    // Acclimation fields
    accl_method: '',
    source_condition: '',
    start_date: '',
    end_date: '',
  });

  // Measurement form state
  const [measFormOpen, setMeasFormOpen] = useState(false);
  const [measSaving, setMeasSaving] = useState(false);
  const [measError, setMeasError] = useState(null);
  const [measPhotoFile, setMeasPhotoFile] = useState(null);
  const [measPhotoPart, setMeasPhotoPart] = useState('');
  const [measForm, setMeasForm] = useState({
    date: new Date().toISOString().split('T')[0],
    height_inches: '',
    longest_leaf_inches: '',
    leaf_count: '',
    notes: '',
  });

  const fetchData = useCallback(() => {
    Promise.all([
      fetch(`/plants/${id}`).then(r => {
        if (!r.ok) throw new Error('Plant not found');
        return r.json();
      }),
      fetch(`/plants/${id}/observations`).then(r => r.json()),
      fetch(`/plants/${id}/measurements`).then(r => r.json()),
      fetch(`/plants/${id}/history`).then(r => r.json()),
      fetch(`/plants/${id}/photos`).then(r => r.json()),
      fetch(`/plants/${id}/dispositions`).then(r => r.ok ? r.json() : []),
      fetch(`/plants/${id}/loans`).then(r => r.ok ? r.json() : []),
    ])
      .then(([plantData, obsData, measData, histData, photoData, dispositionData, loanData]) => {
        setPlant(plantData);
        setObservations(obsData);
        setMeasurements(measData);
        setHistory(histData);
        setPhotos(photoData);
        setDispositions(dispositionData);
        setLoans(loanData);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [id]);

  useEffect(() => {
    fetchData();
    window.scrollTo(0, 0);
    // Reset lineage so it re-fetches for the new plant
    setLineage(null);
    setLineageOpen(false);
    setLineageError(null);
    // Fetch care profile in parallel
    setCareProfileLoading(true);
    fetchCareProfile(id)
      .then(data => { setCareProfile(data); setCareProfileLoading(false); })
      .catch(() => { setCareProfile(null); setCareProfileLoading(false); });
  }, [fetchData, id]);

  useEffect(() => {
    if (!scrollToStatusAfterRefresh.current) return;
    scrollToStatusAfterRefresh.current = false;
    setTimeout(() => {
      statusDetailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
  }, [dispositions, loans]);

  useEffect(() => {
    if (!lightboxPhoto || typeof document === 'undefined') return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [lightboxPhoto]);

  function plantSearchLabel(match) {
    return `${match.specimen_id} · ${plantDisplayName(match)}`;
  }

  function beginEditDisposition(disposition) {
    setEditingDispositionId(disposition.id);
    setDispositionEditError('');
    setDispositionEditForm({
      date: disposition.date || '',
      counterparty_name: disposition.counterparty_name || '',
      counterparty_contact: disposition.counterparty_contact || '',
      amount: disposition.amount ?? '',
      received_plant_id: disposition.received_plant_id || null,
      received_description: disposition.received_description || '',
      loss_reason: disposition.loss_reason || '',
      notes: disposition.notes || '',
    });
    setDispositionSwapSearch(
      disposition.received_plant_id
        ? plantSearchLabel({
            specimen_id: disposition.received_specimen_id || 'Linked plant',
            common_name: disposition.received_common_name,
            genus: disposition.received_genus || '',
            species: disposition.received_species || '',
          })
        : ''
    );
    setDispositionSwapMatches([]);
  }

  function cancelEditDisposition() {
    setEditingDispositionId(null);
    setDispositionEditForm({});
    setDispositionEditError('');
    setDispositionSwapSearch('');
    setDispositionSwapMatches([]);
  }

  function updateDispositionEditField(field, value) {
    setDispositionEditForm(prev => ({ ...prev, [field]: value }));
  }

  function handleDispositionSwapSearch(value) {
    setDispositionSwapSearch(value);
    setDispositionEditForm(prev => ({ ...prev, received_plant_id: null }));
    if (value.trim().length < 2) {
      setDispositionSwapMatches([]);
      return;
    }
    fetch(`/plants?q=${encodeURIComponent(value.trim())}`)
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        setDispositionSwapMatches((data || []).filter(match => String(match.id) !== String(id)));
      })
      .catch(() => setDispositionSwapMatches([]));
  }

  function saveDispositionEdit(disposition) {
    setDispositionEditSaving(true);
    setDispositionEditError('');
    const body = {
      date: dispositionEditForm.date || null,
      counterparty_name: dispositionEditForm.counterparty_name || null,
      counterparty_contact: dispositionEditForm.counterparty_contact || null,
      amount: dispositionEditForm.amount === '' ? null : Number(dispositionEditForm.amount),
      received_description: dispositionEditForm.received_description || null,
      loss_reason: dispositionEditForm.loss_reason || null,
      notes: dispositionEditForm.notes || null,
    };
    if (disposition.type === 'swapped') {
      body.received_plant_id = dispositionEditForm.received_plant_id || null;
    }

    fetch(`/plant-dispositions/${disposition.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then(r => {
        if (!r.ok) return r.json().then(e => { throw new Error(e.detail || 'Failed to update status details'); });
        return r.json();
      })
      .then(() => {
        setDispositionEditSaving(false);
        cancelEditDisposition();
        fetchData();
      })
      .catch(err => {
        setDispositionEditSaving(false);
        setDispositionEditError(err.message);
      });
  }

  function addReceivedSwapPlant(disposition) {
    navigate('/new', {
      state: {
        prefill: {
          status: 'collection',
          acquisition_date: disposition.date || '',
          source: disposition.counterparty_name || 'Swap',
          acquisition_notes: `Received via swap for ${plant.specimen_id}`,
        },
        swap_disposition_id: disposition.id,
        swap_source_plant_id: plant.id,
        swap_source_specimen_id: plant.specimen_id,
      },
    });
  }

  function beginReturnLoan(loan) {
    setReturningLoanId(loan.id);
    setLoanReturnError('');
    const today = new Date().toISOString().slice(0, 10);
    setLoanReturnForm({
      returned_at: today,
      restore_status: loan.direction === 'loaned_out' ? 'collection' : 'collection',
    });
  }

  function cancelReturnLoan() {
    setReturningLoanId(null);
    setLoanReturnForm({});
    setLoanReturnError('');
  }

  function saveReturnLoan(loan) {
    setLoanReturnSaving(true);
    setLoanReturnError('');
    fetch(`/plant-loans/${loan.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        returned_at: loanReturnForm.returned_at || null,
        restore_status: loanReturnForm.restore_status || null,
      }),
    })
      .then(r => {
        if (!r.ok) return r.json().then(e => { throw new Error(e.detail || 'Failed to mark loan as returned'); });
        return r.json();
      })
      .then(() => {
        setLoanReturnSaving(false);
        cancelReturnLoan();
        fetchData();
      })
      .catch(err => {
        setLoanReturnSaving(false);
        setLoanReturnError(err.message);
      });
  }

  // Show toast notification when arriving from a save
  useEffect(() => {
    if (location.state?.swapLinkFailed) {
      setSwapLinkWarning({
        sourcePlantId: location.state.swapSourcePlantId,
        sourceSpecimenId: location.state.swapSourceSpecimenId,
      });
      window.history.replaceState({}, '');
    }
    if (location.state?.fromPropagation) {
      setToast(`Child plant created — ${location.state.specimenId}`);
      const timer = setTimeout(() => setToast(null), 4000);
      window.history.replaceState({}, '');
      return () => clearTimeout(timer);
    } else if (location.state?.saved) {
      setToast('Plant saved successfully');
      const timer = setTimeout(() => setToast(null), 3000);
      window.history.replaceState({}, '');
      return () => clearTimeout(timer);
    }
  }, [location.state]);

  const fromWishlist = location.state?.fromWishlist || (plant?.status === 'wishlist');
  const fromExplore = location.state?.fromExplore;
  const isWishlistPlant = plant?.status === 'wishlist';
  const isArchivedPlant = statusByKey[plant?.status]?.is_archived ?? ['sold', 'gifted', 'swapped', 'deceased'].includes(plant?.status);
  const canAddCareRecords = !isWishlistPlant && !isArchivedPlant;

  const handleObsChange = (e) => {
    const { name, value } = e.target;
    if (name === 'event_type' && value === 'substrate_change') {
      setObsForm(prev => ({
        ...prev,
        event_type: value,
        old_growing_method: plant?.growing_method || '',
        old_substrate: plant?.substrate || '',
      }));
      return;
    }
    setObsForm(prev => ({ ...prev, [name]: value }));
  };

  const resetObsForm = () => {
    setObsForm(prev => ({
      ...prev,
      event_type: 'general_note',
      date: new Date().toISOString().split('T')[0],
      notes: '',
      new_pot_size: '', pot_type: '', new_growing_method_repot: '', new_substrate_repot: '', root_condition: '',
      fert_product: '', concentration: '', fert_method: '',
      treatment_category: '', target: '', treat_product: '', treat_method: '',
      prop_repro_type: 'clone',
      prop_method: 'cutting',
      prop_pollination_type: 'self',
      prop_second_parent: null, prop_mother_is_current: true,
      prop_create_child: true, prop_batch_mode: false, prop_override_name: false,
      child_genus: '', child_species: '', child_common_name: '', child_status: 'propagation',
      prop_rooting_hormone: '', prop_hormone_concentration: '', prop_wound_treatment: [],
      old_growing_method: '', old_substrate: '', new_growing_method: '', new_substrate: '', change_reason: '',
      accl_method: '', source_condition: '', start_date: '', end_date: '',
    }));
    setObsFormOpen(false);
    setObsError(null);
    setObsPhotoFile(null);
    setObsPhotoPart('');
  };

  const handleObsSubmit = (e) => {
    e.preventDefault();
    setObsError(null);
    setObsSaving(true);

    const eventType = obsForm.event_type;

    // Build the payload
    const payload = {
      plant_id: parseInt(id),
      date: obsForm.date,
      event_type: eventType,
      notes: obsForm.notes.trim() || null,
    };

    // Attach detail block based on event type
    if (eventType === 'repot') {
      payload.repot = {
        new_pot_size: obsForm.new_pot_size ? parseFloat(obsForm.new_pot_size) : null,
        pot_type: obsForm.pot_type || null,
        new_growing_method: obsForm.new_growing_method_repot || null,
        new_substrate: obsForm.new_substrate_repot || null,
        root_condition: obsForm.root_condition || null,
      };
    } else if (eventType === 'fertilize') {
      payload.fertilize = {
        product: obsForm.fert_product || null,
        concentration: obsForm.concentration || null,
        method: obsForm.fert_method || null,
      };
    } else if (eventType === 'treatment') {
      payload.treatment = {
        treatment_category: obsForm.treatment_category || null,
        target: obsForm.target || null,
        product: obsForm.treat_product || null,
        method: obsForm.treat_method || null,
      };
    } else if (eventType === 'propagation') {
      const isSeed = obsForm.prop_repro_type === 'seed';
      const isCross = isSeed && obsForm.prop_pollination_type === 'cross';
      const method = isSeed ? methodForPollinationType(obsForm.prop_pollination_type) : obsForm.prop_method;

      const seedParent  = obsForm.prop_mother_is_current ? plant : obsForm.prop_second_parent;
      const pollenDonor = obsForm.prop_mother_is_current ? obsForm.prop_second_parent : plant;

      const motherId = seedParent?.id  === plant.id ? parseInt(id) : obsForm.prop_second_parent?.id || null;
      const fatherId = pollenDonor?.id === plant.id ? parseInt(id) : obsForm.prop_second_parent?.id || null;

      // Strip cultivar, subspecies, variety, and forma tags from a species epithet
      // before hybrid assembly — these are parent-specific and don't carry over.
      const stripEpithet = (sp) => {
        if (!sp) return '';
        return sp
          .replace(/\s*'[^']*'/g, '')       // remove 'Cultivar Name'
          .replace(/\s+(subsp|var|f)\.\s+\S+/g, '')  // remove subsp./var./f. tags
          .trim();
      };

      // Assemble hybrid name from both parents for sexual propagation
      let assembledGenus = null;
      let assembledSpecies = null;
      if (isCross && seedParent && pollenDonor && obsForm.prop_create_child) {
        const seedSpecies   = stripEpithet(seedParent.species);
        const pollenSpecies = stripEpithet(pollenDonor.species);
        const isIntergeneric = seedParent.genus.trim().toLowerCase() !== pollenDonor.genus.trim().toLowerCase();
        if (isIntergeneric) {
          assembledGenus   = `${seedParent.genus.trim()} × ${pollenDonor.genus.trim()}`;
          assembledSpecies = seedSpecies;
        } else {
          assembledGenus   = seedParent.genus.trim();
          assembledSpecies = `${seedSpecies} × ${pollenSpecies}`;
        }
      }

      // Override name takes precedence over auto-assembly when user has edited the fields
      const finalGenus   = obsForm.prop_override_name && obsForm.child_genus   ? obsForm.child_genus.trim()   : assembledGenus;
      const finalSpecies = obsForm.prop_override_name && obsForm.child_species  ? obsForm.child_species.trim()  : assembledSpecies;

      const woundArr = obsForm.prop_wound_treatment || [];
      payload.propagation = {
        method,
        reproduction_category: isSeed ? 'seed' : 'clone',
        pollination_type: isSeed ? obsForm.prop_pollination_type : null,
        parent_plant_id: motherId,
        second_parent_id: isCross ? fatherId : null,
        rooting_hormone: (!isSeed && obsForm.prop_rooting_hormone) ? obsForm.prop_rooting_hormone : null,
        hormone_concentration: (!isSeed && obsForm.prop_rooting_hormone && obsForm.prop_hormone_concentration)
          ? obsForm.prop_hormone_concentration.trim() : null,
        wound_treatment: (!isSeed && WOUND_TREATMENT_METHODS.has(method) && woundArr.length)
          ? JSON.stringify(woundArr) : null,
        create_child_plant: !obsForm.prop_batch_mode && obsForm.prop_create_child,
        child_genus: finalGenus,
        child_species: finalSpecies,
        child_common_name: (!obsForm.prop_batch_mode && obsForm.prop_create_child && obsForm.prop_override_name && obsForm.child_common_name)
          ? obsForm.child_common_name.trim() : null,
        child_status: (!obsForm.prop_batch_mode && obsForm.prop_create_child) ? obsForm.child_status : undefined,
      };
    } else if (eventType === 'substrate_change') {
      payload.substrate_change = {
        old_growing_method: obsForm.old_growing_method || null,
        old_substrate: obsForm.old_substrate || null,
        new_growing_method: obsForm.new_growing_method || null,
        new_substrate: obsForm.new_substrate || null,
        reason: obsForm.change_reason || null,
      };
    } else if (eventType === 'acclimation') {
      payload.acclimation = {
        method: obsForm.accl_method || null,
        source_condition: obsForm.source_condition || null,
        start_date: obsForm.start_date || null,
        end_date: obsForm.end_date || null,
      };
    }

    fetch('/observations', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(response => {
        if (!response.ok) throw new Error('Failed to create observation');
        return response.json();
      })
      .then((newObs) => {
        // Upload attached photo if one was selected
        const photoPromise = obsPhotoFile
          ? uploadLinkedPhoto(obsPhotoFile, {
              observationId: newObs.id,
              photoPart: obsPhotoPart,
              plantIdOverride: newObs.child_plant_id || null,
              targetPlant: newObs.child_plant_id ? 'child' : null,
            })
          : Promise.resolve();

        return photoPromise.then(() => {
          resetObsForm();
          fetch(`/plants/${id}/observations`).then(r => r.json()).then(setObservations);
          fetch(`/plants/${id}/photos`).then(r => r.json()).then(setPhotos);

          // Navigate to the new child plant if one was created
          if (newObs.child_plant_id) {
            navigate(`/plants/${newObs.child_plant_id}`, {
              state: { saved: true, fromPropagation: true, specimenId: newObs.child_specimen_id }
            });
          }
        });
      })
      .catch(err => {
        setObsError(err.message);
        setObsSaving(false);
      });

    setObsSaving(false);
  };

  const handleMeasChange = (e) => {
    const { name, value } = e.target;
    setMeasForm(prev => ({ ...prev, [name]: value }));
  };

  const resetMeasForm = () => {
    setMeasForm({
      date: new Date().toISOString().split('T')[0],
      height_inches: '',
      longest_leaf_inches: '',
      leaf_count: '',
      notes: '',
    });
    setMeasFormOpen(false);
    setMeasError(null);
    setMeasPhotoFile(null);
    setMeasPhotoPart('');
  };

  const handleMeasSubmit = (e) => {
    e.preventDefault();
    setMeasError(null);
    setMeasSaving(true);

    const payload = {
      plant_id: parseInt(id),
      date: measForm.date,
      height_inches: measForm.height_inches ? parseFloat(measForm.height_inches) : null,
      longest_leaf_inches: measForm.longest_leaf_inches ? parseFloat(measForm.longest_leaf_inches) : null,
      leaf_count: measForm.leaf_count ? parseInt(measForm.leaf_count) : null,
      notes: measForm.notes.trim() || null,
    };

    fetch('/measurements', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(response => {
        if (!response.ok) throw new Error('Failed to create measurement');
        return response.json();
      })
      .then((newMeas) => {
        const photoPromise = measPhotoFile
          ? uploadLinkedPhoto(measPhotoFile, { measurementId: newMeas.id, photoPart: measPhotoPart })
          : Promise.resolve();

        return photoPromise.then(() => {
          resetMeasForm();
          fetch(`/plants/${id}/measurements`).then(r => r.json()).then(setMeasurements);
          fetch(`/plants/${id}/photos`).then(r => r.json()).then(setPhotos);
        });
      })
      .catch(err => {
        setMeasError(err.message);
        setMeasSaving(false);
      });

    setMeasSaving(false);
  };

  const openChildPanel = (obs) => {
    setChildPanelObsId(obs.id);
    setChildPanelError(null);
    setChildPanelForm({
      child_status: 'propagation',
      override_name: false,
      child_genus: plant?.genus || '',
      child_species: plant?.species || '',
      child_common_name: '',
    });
  };

  const handleCreateChild = (obs) => {
    const observationId = obs.id;
    if (
      obs.propagation &&
      categoryFromPropagation(obs.propagation) === 'seed' &&
      pollinationFromPropagation(obs.propagation) === 'cross'
    ) {
      const seedLabel = obs.propagation.parent_plant?.specimen_id || 'seed parent';
      const pollenLabel = obs.propagation.second_parent?.specimen_id || 'pollen donor';
      setPendingConfirm({
        type: 'childCreate',
        id: observationId,
        message: `Create a child plant from ${seedLabel} × ${pollenLabel}? The child name will use the stored seed and pollen parents unless you override it.`,
      });
      return;
    }

    doCreateChild(obs);
  };

  const doCreateChild = (obs) => {
    const observationId = obs.id;
    setChildPanelSaving(true);
    setChildPanelError(null);

    const body = { child_status: childPanelForm.child_status };
    if (childPanelForm.override_name) {
      if (childPanelForm.child_genus)       body.child_genus       = childPanelForm.child_genus.trim();
      if (childPanelForm.child_species)     body.child_species     = childPanelForm.child_species.trim();
      if (childPanelForm.child_common_name) body.child_common_name = childPanelForm.child_common_name.trim();
    }

    fetch(`/observations/${observationId}/create-child`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
      .then(r => {
        if (!r.ok) return r.json().then(e => { throw new Error(e.detail || 'Failed to create child plant'); });
        return r.json();
      })
      .then(data => {
        setChildPanelObsId(null);
        setChildPanelSaving(false);
        navigate(`/plants/${data.child_plant_id}`, {
          state: { fromPropagation: true, specimenId: data.child_specimen_id }
        });
      })
      .catch(err => {
        setChildPanelError(err.message);
        setChildPanelSaving(false);
      });
  };

  const formatPlantSearchLabel = (p) => {
    const botanical = [p.genus, p.species].filter(Boolean).join(' ');
    const trade = p.trade_name ? `${botanical ? ' ' : ''}${p.trade_name}` : '';
    const name = `${botanical}${trade}`.trim();
    return `${p.specimen_id} — ${name}${p.common_name ? ` (${p.common_name})` : ''}`;
  };

  const loadSecondParentOptions = (q = '') => {
    const term = q.trim();
    const url = term ? `/plants?q=${encodeURIComponent(term)}` : '/plants';
    fetch(url)
      .then(r => r.json())
      .then(results => setParentSearchResults(results.filter(p => p.id !== parseInt(id)).slice(0, 20)))
      .catch(() => setParentSearchResults([]));
  };

  const searchSecondParent = (q) => {
    setParentSearchQuery(q);
    loadSecondParentOptions(q);
  };

  const loadEditSecondParentOptions = (q = '') => {
    const term = q.trim();
    const url = term ? `/plants?q=${encodeURIComponent(term)}` : '/plants';
    fetch(url)
      .then(r => r.json())
      .then(results => setEditParentSearchResults(results.filter(p => p.id !== parseInt(id)).slice(0, 20)))
      .catch(() => setEditParentSearchResults([]));
  };

  const searchEditSecondParent = (q) => {
    setEditParentSearchQuery(q);
    loadEditSecondParentOptions(q);
  };

  const handleToggleLineage = () => {
    if (!lineageOpen && lineage === null) {
      // Lazy fetch on first expand
      setLineageLoading(true);
      setLineageError(null);
      fetch(`/plants/${id}/lineage`)
        .then(r => { if (!r.ok) throw new Error('Failed to load lineage'); return r.json(); })
        .then(data => { setLineage(data.roots || []); setLineageLoading(false); })
        .catch(err => { setLineageError(err.message); setLineageLoading(false); });
    }
    setLineageOpen(prev => !prev);
  };

  const handleDeleteObs = (obsId) => {
    setPendingConfirm({ type: 'obs', id: obsId });
  };

  const confirmDeleteObs = (obsId) => {
    setPendingConfirm(null);
    fetch(`/observations/${obsId}`, { method: 'DELETE' })
      .then(response => {
        if (!response.ok) throw new Error('Failed to delete observation');
        fetch(`/plants/${id}/observations`).then(r => r.json()).then(setObservations);
      })
      .catch(err => setError(err.message));
  };

  const handleDeleteMeas = (measId) => {
    setPendingConfirm({ type: 'meas', id: measId });
  };

  const confirmDeleteMeas = (measId) => {
    setPendingConfirm(null);
    fetch(`/measurements/${measId}`, { method: 'DELETE' })
      .then(response => {
        if (!response.ok) throw new Error('Failed to delete measurement');
        fetch(`/plants/${id}/measurements`).then(r => r.json()).then(setMeasurements);
      })
      .catch(err => setError(err.message));
  };

  // Inline measurement editing
  const [editingMeasId, setEditingMeasId] = useState(null);
  const [editMeasForm, setEditMeasForm] = useState({});
  const [editMeasPhotoFile, setEditMeasPhotoFile] = useState(null);
  const [editMeasPhotoPart, setEditMeasPhotoPart] = useState('');

  const startEditMeas = (m) => {
    setEditingMeasId(m.id);
    setEditMeasForm({
      date: m.date,
      height_inches: m.height_inches != null ? String(m.height_inches) : '',
      longest_leaf_inches: m.longest_leaf_inches != null ? String(m.longest_leaf_inches) : '',
      leaf_count: m.leaf_count != null ? String(m.leaf_count) : '',
    });
  };

  const handleEditMeasChange = (e) => {
    const { name, value } = e.target;
    setEditMeasForm(prev => ({ ...prev, [name]: value }));
  };

  const saveEditMeas = () => {
    const payload = {
      date: editMeasForm.date,
      height_inches: editMeasForm.height_inches ? parseFloat(editMeasForm.height_inches) : null,
      longest_leaf_inches: editMeasForm.longest_leaf_inches ? parseFloat(editMeasForm.longest_leaf_inches) : null,
      leaf_count: editMeasForm.leaf_count ? parseInt(editMeasForm.leaf_count) : null,
    };

    fetch(`/measurements/${editingMeasId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(response => {
        if (!response.ok) throw new Error('Failed to update measurement');
        return response.json();
      })
      .then(() => {
        const photoPromise = editMeasPhotoFile
          ? uploadLinkedPhoto(editMeasPhotoFile, { measurementId: editingMeasId, photoPart: editMeasPhotoPart })
          : Promise.resolve();

        return photoPromise.then(() => {
          const savedMeasId = editingMeasId;
          setEditingMeasId(null);
          setEditMeasPhotoFile(null);
          setEditMeasPhotoPart('');
          setMeasHistories(p => { const n = { ...p }; delete n[savedMeasId]; return n; });
          fetch(`/plants/${id}/measurements`).then(r => r.json()).then(setMeasurements);
          fetch(`/plants/${id}/photos`).then(r => r.json()).then(setPhotos);
        });
      })
      .catch(err => setError(err.message));
  };

  function toggleObsHistory(obsId) {
    setObsHistories(prev => {
      const entry = prev[obsId];
      if (entry) return { ...prev, [obsId]: { ...entry, open: !entry.open } };
      fetch(`/observations/${obsId}/history`).then(r => r.json()).then(items => {
        setObsHistories(p => ({ ...p, [obsId]: { open: true, items } }));
      });
      return { ...prev, [obsId]: { open: true, items: null } };
    });
  }

  function toggleMeasHistory(measId) {
    setMeasHistories(prev => {
      const entry = prev[measId];
      if (entry) return { ...prev, [measId]: { ...entry, open: !entry.open } };
      fetch(`/measurements/${measId}/history`).then(r => r.json()).then(items => {
        setMeasHistories(p => ({ ...p, [measId]: { open: true, items } }));
      });
      return { ...prev, [measId]: { open: true, items: null } };
    });
  }

  // Inline observation editing
  const [editingObsId, setEditingObsId] = useState(null);
  // Inline create-child panel state — keyed by observation id
  const [childPanelObsId, setChildPanelObsId] = useState(null);
  // Second parent plant search for sexual propagation
  const [parentSearchResults, setParentSearchResults] = useState([]);
  const [parentSearchQuery, setParentSearchQuery] = useState('');
  const [editParentSearchResults, setEditParentSearchResults] = useState([]);
  const [editParentSearchQuery, setEditParentSearchQuery] = useState('');
  const [childPanelForm, setChildPanelForm] = useState({
    child_status: 'propagation',
    override_name: false,
    child_genus: '',
    child_species: '',
    child_common_name: '',
  });
  const [childPanelSaving, setChildPanelSaving] = useState(false);
  const [childPanelError, setChildPanelError] = useState(null);
  const [editObsForm, setEditObsForm] = useState({});
  const [editObsPhotoFile, setEditObsPhotoFile] = useState(null);
  const [editObsPhotoPart, setEditObsPhotoPart] = useState('');

  const startEditObs = (obs) => {
    const currentPlantId = parseInt(id);
    const propagation = obs.propagation || {};
    const parentPlantId = propagation.parent_plant_id || null;
    const secondParentId = propagation.second_parent_id || null;
    const parentPlant = propagation.parent_plant || null;
    const secondParent = propagation.second_parent || null;
    const otherParent = parentPlantId === currentPlantId ? secondParent : parentPlant;
    const currentIsSeedParent = parentPlantId ? parentPlantId === currentPlantId : true;

    setEditingObsId(obs.id);
    setEditObsForm({
      event_type: obs.event_type,
      date: obs.date,
      notes: obs.notes || '',
      // Repot
      new_pot_size: obs.repot?.new_pot_size != null ? String(obs.repot.new_pot_size) : '',
      pot_type: obs.repot?.pot_type || '',
      new_growing_method_repot: obs.repot?.new_growing_method || '',
      new_substrate_repot: obs.repot?.new_substrate || '',
      root_condition: obs.repot?.root_condition || '',
      // Fertilize
      fert_product: obs.fertilize?.product || '',
      concentration: obs.fertilize?.concentration || '',
      fert_method: obs.fertilize?.method || '',
      // Treatment
      treatment_category: obs.treatment?.treatment_category || '',
      target: obs.treatment?.target || '',
      treat_product: obs.treatment?.product || '',
      treat_method: obs.treatment?.method || '',
      // Propagation
      prop_method: propagation.method || 'cutting',
      prop_repro_type: categoryFromPropagation(propagation),
      prop_pollination_type: pollinationFromPropagation(propagation),
      prop_second_parent: otherParent,
      prop_mother_is_current: currentIsSeedParent,
      original_parent_plant_id: parentPlantId,
      original_second_parent_id: secondParentId,
      child_plant_id: obs.child_plant_id || null,
      child_specimen_id: obs.child_specimen_id || null,
      has_child_plant: Boolean(obs.child_plant_id),
      prop_rooting_hormone: propagation.rooting_hormone || '',
      prop_hormone_concentration: propagation.hormone_concentration || '',
      prop_wound_treatment: (() => { try { return propagation.wound_treatment ? JSON.parse(propagation.wound_treatment) : []; } catch { return []; } })(),
      // Substrate change
      old_growing_method: obs.substrate_change?.old_growing_method || '',
      old_substrate: obs.substrate_change?.old_substrate || '',
      new_growing_method: obs.substrate_change?.new_growing_method || '',
      new_substrate: obs.substrate_change?.new_substrate || '',
      change_reason: obs.substrate_change?.reason || '',
      // Acclimation
      accl_method: obs.acclimation?.method || '',
      source_condition: obs.acclimation?.source_condition || '',
      start_date: obs.acclimation?.start_date || '',
      end_date: obs.acclimation?.end_date || '',
    });
    setEditParentSearchQuery(otherParent ? formatPlantSearchLabel(otherParent) : '');
    setEditParentSearchResults([]);
  };

  const handleEditObsChange = (e) => {
    const { name, value } = e.target;
    setEditObsForm(prev => ({ ...prev, [name]: value }));
  };

  const saveEditObs = () => {
    const et = editObsForm.event_type;
    const payload = {
      date: editObsForm.date,
      notes: editObsForm.notes.trim() || null,
    };

    if (et === 'repot') {
      payload.repot = {
        new_pot_size: editObsForm.new_pot_size ? parseFloat(editObsForm.new_pot_size) : null,
        pot_type: editObsForm.pot_type || null,
        new_growing_method: editObsForm.new_growing_method_repot || null,
        new_substrate: editObsForm.new_substrate_repot || null,
        root_condition: editObsForm.root_condition || null,
      };
    } else if (et === 'fertilize') {
      payload.fertilize = {
        product: editObsForm.fert_product || null,
        concentration: editObsForm.concentration || null,
        method: editObsForm.fert_method || null,
      };
    } else if (et === 'treatment') {
      payload.treatment = {
        treatment_category: editObsForm.treatment_category || null,
        target: editObsForm.target || null,
        product: editObsForm.treat_product || null,
        method: editObsForm.treat_method || null,
      };
    } else if (et === 'propagation') {
      const isSeed = editObsForm.prop_repro_type === 'seed';
      const isCross = isSeed && editObsForm.prop_pollination_type === 'cross';
      const method = isSeed ? methodForPollinationType(editObsForm.prop_pollination_type) : editObsForm.prop_method;
      let parentPlantId = isSeed ? parseInt(id) : (editObsForm.original_parent_plant_id || parseInt(id));
      let secondParentId = null;

      if (isCross) {
        if (!editObsForm.prop_second_parent) {
          setError('Choose the other parent plant before saving this seed cross.');
          return;
        }
        const seedParent = editObsForm.prop_mother_is_current ? plant : editObsForm.prop_second_parent;
        const pollenDonor = editObsForm.prop_mother_is_current ? editObsForm.prop_second_parent : plant;
        parentPlantId = seedParent?.id === plant?.id ? parseInt(id) : editObsForm.prop_second_parent.id;
        secondParentId = pollenDonor?.id === plant?.id ? parseInt(id) : editObsForm.prop_second_parent.id;
      }

      const editWoundArr = editObsForm.prop_wound_treatment || [];
      payload.propagation = {
        method,
        reproduction_category: isSeed ? 'seed' : 'clone',
        pollination_type: isSeed ? editObsForm.prop_pollination_type : null,
        parent_plant_id: parentPlantId,
        second_parent_id: secondParentId,
        rooting_hormone: (!isSeed && editObsForm.prop_rooting_hormone) ? editObsForm.prop_rooting_hormone : null,
        hormone_concentration: (!isSeed && editObsForm.prop_rooting_hormone && editObsForm.prop_hormone_concentration)
          ? editObsForm.prop_hormone_concentration.trim() : null,
        wound_treatment: (!isSeed && WOUND_TREATMENT_METHODS.has(method) && editWoundArr.length)
          ? JSON.stringify(editWoundArr) : null,
      };

      if (
        editObsForm.has_child_plant &&
        (parentPlantId !== editObsForm.original_parent_plant_id || secondParentId !== editObsForm.original_second_parent_id)
      ) {
        setPendingConfirm({
          type: 'parentEdit',
          message: 'This observation already has a linked child plant. Updating the seed or pollen parent will update the mirrored propagation record, but it will not rename the existing child plant.',
          payload,
          obsId: editingObsId,
        });
        return;
      }
    } else if (et === 'substrate_change') {
      payload.substrate_change = {
        old_growing_method: editObsForm.old_growing_method || null,
        old_substrate: editObsForm.old_substrate || null,
        new_growing_method: editObsForm.new_growing_method || null,
        new_substrate: editObsForm.new_substrate || null,
        reason: editObsForm.change_reason || null,
      };
    } else if (et === 'acclimation') {
      payload.acclimation = {
        method: editObsForm.accl_method || null,
        source_condition: editObsForm.source_condition || null,
        start_date: editObsForm.start_date || null,
        end_date: editObsForm.end_date || null,
      };
    }

    submitObsPayload(editingObsId, payload);
  };

  const submitObsPayload = (obsId, payload) => {
    setPendingConfirm(null);
    fetch(`/observations/${obsId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
      .then(response => {
        if (!response.ok) throw new Error('Failed to update observation');
        return response.json();
      })
      .then(() => {
        const photoPromise = editObsPhotoFile
          ? uploadLinkedPhoto(editObsPhotoFile, { observationId: obsId, photoPart: editObsPhotoPart })
          : Promise.resolve();

        return photoPromise.then(() => {
          setEditingObsId(null);
          setEditObsPhotoFile(null);
          setEditObsPhotoPart('');
          setObsHistories(p => { const n = { ...p }; delete n[obsId]; return n; });
          fetch(`/plants/${id}`).then(r => r.json()).then(setPlant);
          fetch(`/plants/${id}/observations`).then(r => r.json()).then(setObservations);
          fetch(`/plants/${id}/photos`).then(r => r.json()).then(setPhotos);
        });
      })
      .catch(err => setError(err.message));
  };

  // -- Photo handlers -------------------------------------------------------

  const uploadLinkedPhoto = (file, { observationId = null, measurementId = null, plantIdOverride = null, targetPlant = null, photoPart: selectedPhotoPart = '' } = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    if (selectedPhotoPart) formData.append('photo_part', selectedPhotoPart);
    if (observationId) formData.append('observation_id', observationId);
    if (measurementId) formData.append('measurement_id', measurementId);
    if (targetPlant) formData.append('target_plant', targetPlant);

    const targetPlantId = plantIdOverride || id;
    return fetch(`/plants/${targetPlantId}/photos`, {
      method: 'POST',
      body: formData,
    }).then(response => {
      if (!response.ok) throw new Error('Failed to upload photo');
      return response.json();
    });
  };

  const uploadFile = (file) => {
    if (!file || !file.type.startsWith('image/')) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    fetch(`/plants/${id}/photos`, {
      method: 'POST',
      body: formData,
    })
      .then(response => {
        if (!response.ok) throw new Error('Failed to upload photo');
        return response.json();
      })
      .then(() => {
        fetch(`/plants/${id}/photos`).then(r => r.json()).then(setPhotos);
        setUploading(false);
      })
      .catch(err => {
        setError(err.message);
        setUploading(false);
      });
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    uploadFile(file);
    // Reset the input so the same file can be re-selected
    e.target.value = '';
  };

  // -- Drag and drop --------------------------------------------------------

  const [dragActive, setDragActive] = useState(false);
  const dragCounter = { current: 0 };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) setDragActive(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    dragCounter.current = 0;

    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  };

  const handleSetCoverPhoto = (photoId) => {
    // Toggle: if this photo is already the cover, clear it; otherwise set it
    const newId = plant.default_photo_id === photoId ? null : photoId;
    fetch(`/plants/${id}/cover-photo`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ photo_id: newId }),
    })
      .then(r => { if (!r.ok) throw new Error('Failed to update cover photo'); return r.json(); })
      .then(() => setPlant(prev => ({ ...prev, default_photo_id: newId, cover_photo_offset_x: 50, cover_photo_offset_y: 50 })))
      .catch(err => setError(err.message));
  };

  // -- Cover photo pan/reposition -------------------------------------------

  const enterPositionMode = () => {
    const x = plant.cover_photo_offset_x ?? 50;
    const y = plant.cover_photo_offset_y ?? 50;
    setDraftOffset({ x, y });
    setDraftZoom(plant.cover_photo_zoom ?? 1);
    setPositionMode(true);
  };

  const cancelPositionMode = () => {
    setPositionMode(false);
    setDraftOffset(null);
    setDraftZoom(null);
    panStartRef.current = null;
  };

  const savePosition = () => {
    if (!draftOffset) return;
    setSavingPosition(true);
    fetch(`/plants/${id}/cover-photo-position`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ x: draftOffset.x, y: draftOffset.y, zoom: draftZoom ?? 1 }),
    })
      .then(r => { if (!r.ok) throw new Error('Failed to save position'); return r.json(); })
      .then(data => {
        setPlant(prev => ({
          ...prev,
          cover_photo_offset_x: data.cover_photo_offset_x,
          cover_photo_offset_y: data.cover_photo_offset_y,
          cover_photo_zoom: data.cover_photo_zoom,
        }));
        setPositionMode(false);
        setDraftOffset(null);
        setDraftZoom(null);
        panStartRef.current = null;
      })
      .catch(err => setError(err.message))
      .finally(() => setSavingPosition(false));
  };

  const handlePanStart = (e) => {
    if (!positionMode) return;
    e.preventDefault();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    panStartRef.current = { clientX, clientY, offsetX: draftOffset.x, offsetY: draftOffset.y };
    if (e.pointerId != null) e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePanMove = (e) => {
    if (!positionMode || !panStartRef.current) return;
    e.preventDefault();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const container = heroPanRef.current;
    if (!container) return;
    const { width, height } = container.getBoundingClientRect();
    const dx = clientX - panStartRef.current.clientX;
    const dy = clientY - panStartRef.current.clientY;
    // Dragging right = pan left (decrease x); dragging down = pan up (decrease y)
    const newX = Math.max(0, Math.min(100, panStartRef.current.offsetX - (dx / width) * 100));
    const newY = Math.max(0, Math.min(100, panStartRef.current.offsetY - (dy / height) * 100));
    setDraftOffset({ x: newX, y: newY });
  };

  const handlePanEnd = () => {
    panStartRef.current = null;
  };

  const handleDeletePhoto = (photoId) => {
    setPendingConfirm({ type: 'photo', id: photoId });
  };

  const confirmDeletePhoto = (photoId) => {
    setPendingConfirm(null);
    fetch(`/photos/${photoId}`, { method: 'DELETE' })
      .then(response => {
        if (!response.ok) throw new Error('Failed to delete photo');
        setPhotos(prev => prev.filter(p => p.id !== photoId));
        if (lightboxPhoto?.id === photoId) setLightboxPhoto(null);
        if (plant.default_photo_id === photoId) {
          setPlant(prev => ({ ...prev, default_photo_id: null }));
        }
      })
      .catch(err => setError(err.message));
  };

  const handleRotatePhoto = (photoId) => {
    fetch(`/photos/${photoId}/rotate`, { method: 'POST' })
      .then(r => { if (!r.ok) throw new Error('Failed to rotate photo'); return r.json(); })
      .then(() => {
        // fetchData re-fetches photos with updated rotation_version values.
        // The useEffect on [photos] then syncs lightboxPhoto to the fresh object.
        fetchData();
      })
      .catch(err => setError(err.message));
  };

  const handlePhotoPartChange = (photoId, nextPhotoPart) => {
    fetch(`/photos/${photoId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ photo_part: nextPhotoPart || null }),
    })
      .then(response => {
        if (!response.ok) throw new Error('Failed to update photo label');
        return response.json();
      })
      .then(updatedPhoto => {
        setPhotos(prev => prev.map(photo => photo.id === photoId ? updatedPhoto : photo));
        setLightboxPhoto(prev => prev?.id === photoId ? updatedPhoto : prev);
      })
      .catch(err => setError(err.message));
  };

  const handlePhotoGrowthStageChange = (photoId, nextGrowthStage) => {
    fetch(`/photos/${photoId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ growth_stage: nextGrowthStage || null }),
    })
      .then(response => {
        if (!response.ok) throw new Error('Failed to update foliage stage');
        return response.json();
      })
      .then(updatedPhoto => {
        setPhotos(prev => prev.map(photo => photo.id === photoId ? updatedPhoto : photo));
        setLightboxPhoto(prev => prev?.id === photoId ? updatedPhoto : prev);
      })
      .catch(err => setError(err.message));
  };

  const handleReferenceConsentToggle = (photoId, currentConsent) => {
    fetch(`/photos/${photoId}/reference-consent`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ consent: !currentConsent }),
    })
      .then(r => { if (!r.ok) throw new Error('Failed to update sharing preference'); return r.json(); })
      .then(updated => {
        setPhotos(prev => prev.map(p => p.id === photoId ? updated : p));
        setLightboxPhoto(prev => prev?.id === photoId ? updated : prev);
      })
      .catch(err => setError(err.message));
  };

  // -- Multi-photo identification ---------------------------------------------

  const handleMultiIdentify = () => {
    if (selectedPhotoIds.size === 0) return;
    setMultiIdLoading(true);
    setMultiIdError(null);
    setMultiIdResults(null);

    fetch(`/plants/${id}/identify-multi`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ photo_ids: Array.from(selectedPhotoIds) }),
    })
      .then(r => {
        if (!r.ok) return r.json().then(e => { throw new Error(e.detail || 'Identification failed'); });
        return r.json();
      })
      .then(data => {
        setMultiIdResults(data);
        setMultiIdLoading(false);
        fetch('/id-quota')
          .then(r => r.ok ? r.json() : null)
          .then(q => { if (q) setMultiIdQuotaInfo(q); })
          .catch(() => {});
      })
      .catch(err => {
        setMultiIdError(err.message);
        setMultiIdLoading(false);
      });
  };

  const handleAcceptMultiSuggestion = (suggestion) => {
    setMultiIdConfirm({ resultId: multiIdResults.id, suggestion });
  };

  const handleConfirmAccept = () => {
    if (!multiIdConfirm) return;
    const { resultId, suggestion } = multiIdConfirm;
    fetch(`/api-results/${resultId}/accept`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ suggestion, update_plant: true }),
    })
      .then(r => {
        if (!r.ok) throw new Error('Failed to accept suggestion');
        return r.json();
      })
      .then(() => {
        fetchData();
        setMultiIdConfirm(null);
        setMultiIdResults(null);
        setToast('Plant identification accepted.');
        setTimeout(() => setToast(null), 3000);
      })
      .catch(err => {
        setMultiIdConfirm(null);
        setError(err.message);
      });
  };

  // Prefer the user-selected cover photo; fall back to most-recently-uploaded
  const coverPhotoId = plant?.default_photo_id ?? null;
  const heroPhoto = photos.length > 0
    ? (coverPhotoId ? photos.find(p => p.id === coverPhotoId) ?? photos[0] : photos[0])
    : null;
  const latestPhoto = heroPhoto; // kept for compatibility with existing refs

  // When the photos array refreshes (e.g. after rotation or fetchData), sync the
  // lightbox to the updated photo object so it picks up the new rotation_version
  // and the URL cache-bust works correctly.
  useEffect(() => {
    if (!lightboxPhoto) return;
    const fresh = photos.find(p => p.id === lightboxPhoto.id);
    if (fresh && fresh !== lightboxPhoto) setLightboxPhoto(fresh);
  }, [photos]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCareScheduled = () => {
    setScheduleModalOpen(false);
    setToast('Care task scheduled.');
  };

  const handleCompareProfile = () => {
    const profileId = plant?.profile_id || careProfile?.id || null;
    if (!profileId) {
      setToast('No care profile matched for this plant');
      setTimeout(() => setToast(null), 3000);
      return;
    }
    navigate(`/compare?profiles=${profileId}`, { state: { returnTo: location.pathname } });
  };

  const openConfirmSheet = useCallback(async () => {
    setConfirmSheetOpen(true);
    setIdTraits(null);
    setIdTraitsError(false);
    setTraitsChecked({});
    try {
      const res = await fetch(`/plants/${id}/id-traits`);
      if (res.ok) {
        const data = await res.json();
        setIdTraits(data.traits);
      } else {
        setIdTraitsError(true);
      }
    } catch {
      setIdTraitsError(true);
    }
  }, [id]);

  const handleConfirmId = useCallback(async () => {
    setConfirming(true);
    try {
      const checkedKeys = Object.keys(traitsChecked).filter(k => traitsChecked[k]);
      const res = await fetch(`/plants/${id}/confirm-id`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ traits_confirmed: checkedKeys }),
      });
      if (res.ok) {
        const updated = await res.json();
        setPlant(updated);
        setConfirmSheetOpen(false);
        setToast('Identification confirmed');
        setTimeout(() => setToast(null), 3000);
      }
    } finally {
      setConfirming(false);
    }
  }, [id, traitsChecked]);

  function statusPickerAction(targetKey) {
    if (targetKey === plant.status) return 'current';
    const meta = statusByKey[targetKey];
    if (!meta || meta.requires_disposition) return 'transition';
    if (targetKey === 'loaned_in' || targetKey === 'loaned_out') {
      const hasActiveLoan = loans.some(l => !l.returned_at);
      return hasActiveLoan ? 'loan-blocked' : 'transition';
    }
    if (plant.status === 'wishlist' && targetKey === 'collection') return 'acquire';
    return 'put';
  }

  async function handleStatusChange(targetKey) {
    const action = statusPickerAction(targetKey);
    if (action === 'current' || action === 'loan-blocked') return;
    if (action === 'transition') {
      setStatusPickerOpen(false);
      setTransitionTarget(targetKey);
      return;
    }
    if (action === 'acquire') {
      setStatusPickerOpen(false);
      setAcquireModalOpen(true);
      return;
    }
    setStatusPickerOpen(false);
    setStatusChanging(true);
    try {
      const r = await fetch(`/plants/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: targetKey }),
      });
      if (!r.ok) {
        const err = await r.json();
        throw new Error(err.detail || 'Failed to update status');
      }
      const updated = await r.json();
      setPlant(updated);
      setToast(`Status updated to ${formatStatus(targetKey, statusByKey)}`);
      setTimeout(() => setToast(null), 3000);
    } catch (err) {
      setToast(err.message);
      setTimeout(() => setToast(null), 4000);
    } finally {
      setStatusChanging(false);
    }
  }

  const observationsExpanded = observationsOpen || obsFormOpen || editingObsId != null;
  const measurementsExpanded = measurementsOpen || measFormOpen || editingMeasId != null;

  function openObsForm() {
    setObservationsOpen(true);
    setObsFormOpen(true);
    setTimeout(() => obsFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }
  function openMeasForm() {
    setMeasurementsOpen(true);
    setMeasFormOpen(true);
    setTimeout(() => measFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }
  const visibleObservations = observationsExpanded
    ? observations
    : observations.slice(0, COLLAPSED_ACTIVITY_PREVIEW_COUNT);
  const visibleMeasurements = measurementsExpanded
    ? measurements
    : measurements.slice(0, COLLAPSED_ACTIVITY_PREVIEW_COUNT);
  const hiddenObservationCount = Math.max(0, observations.length - visibleObservations.length);
  const hiddenMeasurementCount = Math.max(0, measurements.length - visibleMeasurements.length);
  const visibleHistory = history.filter(h => !INTERNAL_PLANT_HISTORY_FIELDS.has(h.field_name));

  if (loading) return <SkeletonPlantDetail />;
  if (error) return <div className="app"><ErrorScreen message={error} onRetry={fetchData} /></div>;
  if (!plant) return <div className="app"><p>Plant not found</p></div>;

  const eventType = obsForm.event_type;
  const potTypeOptions = fieldOptionList(fieldOptions, 'repot_pot_type');
  const rootConditionOptions = fieldOptionList(fieldOptions, 'repot_root_condition');
  const fertilizeMethodOptions = fieldOptionList(fieldOptions, 'fertilize_method');
  const treatmentCategoryOptions = fieldOptionList(fieldOptions, 'treatment_category');
  const treatmentMethodOptions = fieldOptionList(fieldOptions, 'treatment_method');
  const propHormoneOptions = fieldOptionList(fieldOptions, 'prop_hormone');
  const lossReasonOptions = fieldOptionList(fieldOptions, 'loss_reason');
  const renderDispositionEditForm = (disposition) => {
    const counterpartyLabel = disposition.type === 'sold'
      ? 'Buyer'
      : disposition.type === 'gifted'
        ? 'Recipient'
        : disposition.type === 'swapped'
          ? 'Swapped With'
          : 'Contact';
    return (
      <div className="status-context-edit">
        <div className="form-grid">
          <div className="form-field">
            <label className="form-label">{disposition.type === 'sold' ? 'Sale Date' : disposition.type === 'swapped' ? 'Swap Date' : disposition.type === 'gifted' ? 'Gift Date' : 'Date'}</label>
            <input
              type="date"
              className="form-input"
              value={dispositionEditForm.date || ''}
              onChange={e => updateDispositionEditField('date', e.target.value)}
            />
          </div>

          {disposition.type === 'sold' && (
            <div className="form-field">
              <label className="form-label">Sale Price ($)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                inputMode="decimal"
                className="form-input"
                value={dispositionEditForm.amount ?? ''}
                onChange={e => updateDispositionEditField('amount', e.target.value)}
              />
            </div>
          )}

          {disposition.type !== 'deceased' && (
            <>
              <div className="form-field">
                <label className="form-label">{counterpartyLabel}</label>
                <input
                  className="form-input"
                  value={dispositionEditForm.counterparty_name || ''}
                  onChange={e => updateDispositionEditField('counterparty_name', e.target.value)}
                  placeholder="Optional name"
                />
              </div>
              <div className="form-field">
                <label className="form-label">Contact</label>
                <input
                  className="form-input"
                  value={dispositionEditForm.counterparty_contact || ''}
                  onChange={e => updateDispositionEditField('counterparty_contact', e.target.value)}
                  placeholder="Optional contact"
                />
              </div>
            </>
          )}

          {disposition.type === 'deceased' && lossReasonOptions.length > 0 && (
            <div className="form-field">
              <label className="form-label">Loss Reason</label>
              <select
                className="form-input"
                value={dispositionEditForm.loss_reason || ''}
                onChange={e => updateDispositionEditField('loss_reason', e.target.value)}
              >
                <option value="">Unknown</option>
                {lossReasonOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          )}

          {disposition.type === 'swapped' && (
            <>
              <div className="form-field">
                <label className="form-label">Received Plant</label>
                <input
                  className="form-input"
                  value={dispositionSwapSearch}
                  onChange={e => handleDispositionSwapSearch(e.target.value)}
                  placeholder="Search existing plant"
                />
                {dispositionEditForm.received_plant_id && (
                  <small className="field-hint">Selected linked plant</small>
                )}
                {dispositionSwapMatches.length > 0 && !dispositionEditForm.received_plant_id && (
                  <div className="duplicate-list status-search-results">
                    {dispositionSwapMatches.slice(0, 5).map(match => (
                      <button
                        key={match.id}
                        type="button"
                        className="duplicate-card"
                        onClick={() => {
                          updateDispositionEditField('received_plant_id', match.id);
                          setDispositionSwapSearch(plantSearchLabel(match));
                          setDispositionSwapMatches([]);
                        }}
                      >
                        <span className="duplicate-name">{plantDisplayName(match)}</span>
                        <span className="duplicate-meta">{match.specimen_id}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="form-field">
                <label className="form-label">Received Description</label>
                <input
                  className="form-input"
                  value={dispositionEditForm.received_description || ''}
                  onChange={e => updateDispositionEditField('received_description', e.target.value)}
                  placeholder="Optional if not linked"
                />
              </div>
            </>
          )}

          <div className="form-field form-field-full">
            <label className="form-label">Notes</label>
            <textarea
              className="form-input"
              rows="2"
              value={dispositionEditForm.notes || ''}
              onChange={e => updateDispositionEditField('notes', e.target.value)}
              placeholder="Optional context"
            />
          </div>
        </div>

        {dispositionEditError && <p className="error">{dispositionEditError}</p>}
        <div className="modal-actions">
          <button type="button" className="btn btn-ghost btn-sm" onClick={cancelEditDisposition} disabled={dispositionEditSaving}>Cancel</button>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => saveDispositionEdit(disposition)} disabled={dispositionEditSaving}>
            {dispositionEditSaving ? 'Saving...' : 'Save Details'}
          </button>
        </div>
      </div>
    );
  };

  const renderLoanReturnForm = (loan) => {
    const todayStr = new Date().toISOString().slice(0, 10);
    const isOverdue = !loan.returned_at && loan.expected_return_at && loan.expected_return_at < todayStr;
    return (
      <div className="status-context-edit">
        <div className="form-grid">
          <div className="form-field">
            <label className="form-label">{loan.direction === 'loaned_out' ? 'Date Returned to You' : 'Date Returned to Owner'}</label>
            <input
              type="date"
              className="form-input"
              value={loanReturnForm.returned_at || ''}
              onChange={e => setLoanReturnForm(prev => ({ ...prev, returned_at: e.target.value }))}
            />
          </div>
          <div className="form-field">
            <label className="form-label">{loan.direction === 'loaned_out' ? 'Restore Plant Status' : 'Status After Return'}</label>
            <select
              className="form-input"
              value={loanReturnForm.restore_status || ''}
              onChange={e => setLoanReturnForm(prev => ({ ...prev, restore_status: e.target.value }))}
            >
              {statusMetadata.filter(s => s.include_in_collection && s.key !== 'loaned_in' && s.key !== 'loaned_out').map(s => (
                <option key={s.key} value={s.key}>{formatStatus(s.key, statusByKey)}</option>
              ))}
            </select>
          </div>
        </div>
        {isOverdue && (
          <p className="status-context-notes" style={{ color: 'var(--warning, #b45309)' }}>
            This loan was expected back on {formatDate(loan.expected_return_at)}.
          </p>
        )}
        {loanReturnError && <p className="error">{loanReturnError}</p>}
        <div className="modal-actions">
          <button type="button" className="btn btn-ghost btn-sm" onClick={cancelReturnLoan} disabled={loanReturnSaving}>Cancel</button>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => saveReturnLoan(loan)} disabled={loanReturnSaving}>
            {loanReturnSaving ? 'Saving...' : 'Mark Returned'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div
      className="app"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* -- Drag overlay ---------------------------------------------------- */}
      {dragActive && (
        <div className="drag-overlay">
          <div className="drag-overlay-content">
            <span className="drag-overlay-icon"><Icon name="camera" size={24} /></span>
            <span className="drag-overlay-text">Drop photo to upload</span>
          </div>
        </div>
      )}

      {/* -- Toast notification ---------------------------------------------- */}
      {toast && (
        <div className="toast" onClick={() => setToast(null)}>
          {toast}
        </div>
      )}

      <header className="sub-header">
        <button className="sub-header-back" onClick={() => navigate(fromWishlist ? '/wishlist' : fromExplore ? fromExplore : (location.state?.returnTo || '/'), { replace: true })} aria-label="Back" title="Back">
          <Icon name="arrowLeft" size={20} />
        </button>
        <div className="sub-header-title">
          <h1>Plant Detail</h1>
          <p className="sub-header-subtitle">{plant.specimen_id}</p>
        </div>
      </header>

      {swapLinkWarning && (
        <div className="status-detail-panel" style={{ marginBottom: '1rem' }}>
          <p className="status-context-line" style={{ marginTop: 0 }}>
            Couldn't link this plant to the swap record.
            {swapLinkWarning.sourcePlantId ? (
              <>
                {' '}Go back to{' '}
                <Link to={`/plants/${swapLinkWarning.sourcePlantId}`}>
                  {swapLinkWarning.sourceSpecimenId || 'the swapped plant'}
                </Link>
                {' '}to link it manually.
              </>
            ) : ' You can link it manually from the swapped plant.'}
          </p>
        </div>
      )}

      {/* -- Hero Photo ----------------------------------------------------- */}
      {latestPhoto ? (() => {
        const activeX    = positionMode && draftOffset ? draftOffset.x : (plant.cover_photo_offset_x ?? 50);
        const activeY    = positionMode && draftOffset ? draftOffset.y : (plant.cover_photo_offset_y ?? 50);
        const activeZoom = positionMode && draftZoom != null ? draftZoom : (plant.cover_photo_zoom ?? 1);

        return (
          <>
            <div
              className={`hero-photo${positionMode ? ' hero-photo--pan-mode' : ''}`}
              ref={heroPanRef}
              onClick={positionMode ? undefined : () => setLightboxPhoto(latestPhoto)}
              onPointerDown={positionMode ? handlePanStart : undefined}
              onPointerMove={positionMode ? handlePanMove : undefined}
              onPointerUp={positionMode ? handlePanEnd : undefined}
              onPointerCancel={positionMode ? handlePanEnd : undefined}
              onTouchStart={positionMode ? handlePanStart : undefined}
              onTouchMove={positionMode ? handlePanMove : undefined}
              onTouchEnd={positionMode ? handlePanEnd : undefined}
            >
              <img
                src={photoUrl(latestPhoto.id, latestPhoto.rotation_version, latestPhoto.created_at)}
                alt={plantDisplayName(plant) || 'Plant photo'}
                className="hero-photo-img"
                style={{
                  objectPosition: `${activeX}% ${activeY}%`,
                  transform: activeZoom !== 1 ? `scale(${activeZoom})` : undefined,
                  transformOrigin: activeZoom !== 1 ? `${activeX}% ${activeY}%` : undefined,
                }}
                draggable={false}
              />
              {!positionMode && photos.length > 1 && (
                <span className="hero-photo-count">{photos.length} photos</span>
              )}
              {!positionMode && (
                <button
                  className="hero-photo-reposition-btn"
                  onClick={e => { e.stopPropagation(); enterPositionMode(); }}
                  title="Adjust visible area"
                  aria-label="Adjust photo position"
                >
                  <Icon name="move" size={14} />
                </button>
              )}
            </div>
            {positionMode && (
              <div className="hero-photo-position-actions">
                <div className="hero-photo-zoom-row">
                  <Icon name="search" size={14} />
                  <input
                    type="range"
                    min="1" max="3" step="0.05"
                    value={activeZoom}
                    onChange={e => setDraftZoom(parseFloat(e.target.value))}
                    className="hero-photo-zoom-slider"
                    aria-label="Zoom level"
                  />
                  <span className="hero-photo-zoom-value">{Math.round(activeZoom * 100)}%</span>
                </div>
                <div className="hero-photo-position-btns">
                  <button className="btn btn-ghost btn-sm" onClick={cancelPositionMode} disabled={savingPosition}>
                    Cancel
                  </button>
                  <button className="btn btn-primary btn-sm" onClick={savePosition} disabled={savingPosition}>
                    {savingPosition ? 'Saving…' : 'Save position'}
                  </button>
                </div>
              </div>
            )}
          </>
        );
      })() : (
        <div className="hero-photo-empty">
          <span className="hero-photo-empty-icon"><Icon name="sprout" size={32} /></span>
          <span className="hero-photo-empty-text">No photos yet</span>
        </div>
      )}

      {/* -- Plant Header -------------------------------------------------- */}
      <div className="detail-header">
        {/* Row 1: specimen ID (left) + status pill (right) */}
        <div className="detail-header-identity">
          <span className="specimen-id">{plant.specimen_id}</span>
          {isArchivedPlant ? (
            <span className={`status-badge status-${plant.status}`} style={statusStyle(plant.status, statusByKey)}>
              {formatStatus(plant.status, statusByKey)}
            </span>
          ) : (
            <button
              type="button"
              className={`status-badge status-badge--btn status-${plant.status}`}
              style={statusStyle(plant.status, statusByKey)}
              onClick={() => setStatusPickerOpen(true)}
              aria-haspopup="dialog"
              aria-expanded={statusPickerOpen}
              disabled={statusChanging}
            >
              {formatStatus(plant.status, statusByKey)}
              <Icon name="chevronDown" size={10} style={{ marginLeft: '3px', verticalAlign: 'middle', opacity: 0.7 }} />
            </button>
          )}
        </div>

        {/* Row 2: equal-width action buttons */}
        <div className="detail-header-actions">
          <label
            className={`btn btn-secondary btn-sm detail-action-btn photo-capture-btn ${uploading ? 'btn-disabled' : ''}`}
            aria-label={uploading ? 'Uploading…' : 'Take photo'}
            title={uploading ? 'Uploading…' : 'Take photo'}
          >
            {uploading ? <Icon name='clock' size={15} /> : <Icon name='camera' size={15} />}
            <span className="detail-action-label">{uploading ? 'Uploading…' : 'Camera'}</span>
            <input type="file" accept="image/*" capture="environment" onChange={handlePhotoUpload} disabled={uploading} style={{ display: 'none' }} />
          </label>
          <label
            className={`btn btn-secondary btn-sm detail-action-btn photo-capture-btn ${uploading ? 'btn-disabled' : ''}`}
            aria-label="Upload from library"
            title="Upload from library"
          >
            <Icon name="image" size={15} />
            <span className="detail-action-label">Gallery</span>
            <input type="file" accept="image/*" onChange={handlePhotoUpload} disabled={uploading} style={{ display: 'none' }} />
          </label>
          <button
            className="btn btn-secondary btn-sm detail-action-btn"
            onClick={handleCompareProfile}
            disabled={careProfileLoading && !plant?.profile_id}
          >
            <Icon name="search" size={15} />
            <span className="detail-action-label">Compare</span>
          </button>
          {/* replace: keeps PlantDetail as the single back-stack entry behind the edit form */}
          <button
            className="btn btn-secondary btn-sm detail-action-btn"
            onClick={() => navigate(`/plants/${id}/edit`, { replace: true })}
          >
            <Icon name="pencil" size={15} />
            <span className="detail-action-label">Edit</span>
          </button>
        </div>

        {/* Identity: common name → source of truth. Trade name → plain h1 + scientific subtitle. Else scientific name as h1. */}
        <h1 className="detail-name">
          {plant.common_name
            ? plant.common_name
            : plant.trade_name
              ? plantDisplayName(plant)
              : <em>{plant.genus} {plant.species}</em>
          }
        </h1>
        {(plant.common_name || plant.trade_name) && (
          <p className="scientific-name"><em>{plant.genus} {plant.species}</em></p>
        )}
        {plant.id_confidence && (
          plant.id_confidence === 'likely' ? (
            <button
              type="button"
              className="id-confidence-chip id-confidence-chip--likely id-confidence-chip--btn"
              onClick={openConfirmSheet}
              aria-haspopup="dialog"
              title="Confirm identification"
              aria-label="Identification likely — tap to confirm"
            >
              Likely
              <Icon name="chevronDown" size={10} style={{ marginLeft: '3px', verticalAlign: 'middle', opacity: 0.7 }} />
            </button>
          ) : (
            <span className={`id-confidence-chip id-confidence-chip--${plant.id_confidence}`}>
              {plant.id_confidence === 'provisional' ? 'Provisional' : 'Confirmed'}
            </span>
          )
        )}
      </div>

      {scheduleModalOpen && (
        <PlantCareScheduleModal
          plant={plant}
          onSaved={handleCareScheduled}
          onCancel={() => setScheduleModalOpen(false)}
        />
      )}

      {/* -- Status picker sheet ------------------------------------------- */}
      <BottomSheet
        open={statusPickerOpen}
        onClose={() => setStatusPickerOpen(false)}
        maxHeight="65dvh"
        title="Change Status"
      >
        <div className="status-picker-body">
          <div className="status-picker-list">
            {statusMetadata.filter(s => s.is_active !== false).map(s => {
              const action = statusPickerAction(s.key);
              const isCurrent = s.key === plant.status;
              return (
                <button
                  key={s.key}
                  type="button"
                  className={`status-picker-row${(isCurrent || action === 'loan-blocked') ? ' status-picker-row--current' : ''}`}
                  onClick={() => handleStatusChange(s.key)}
                  disabled={isCurrent || action === 'loan-blocked' || statusChanging}
                >
                  <span className="status-badge" style={statusStyle(s.key, statusByKey)}>
                    {formatStatus(s.key, statusByKey)}
                  </span>
                  <span className="status-picker-row-desc">
                    {isCurrent
                      ? 'Current status'
                      : action === 'loan-blocked'
                      ? 'Mark active loan as returned first'
                      : action === 'transition'
                      ? 'Add details →'
                      : action === 'acquire'
                      ? 'Enter acquisition details'
                      : null}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </BottomSheet>

      {/* -- Mark acquired modal ------------------------------------------- */}
      {acquireModalOpen && (
        <MarkAcquiredModal
          plant={plant}
          onSuccess={(updated) => {
            setPlant(updated);
            setAcquireModalOpen(false);
            setToast('Marked as acquired');
            setTimeout(() => setToast(null), 3000);
          }}
          onCancel={() => setAcquireModalOpen(false)}
        />
      )}

      {/* -- Status transition sheet --------------------------------------- */}
      {transitionTarget && (
        <StatusTransitionSheet
          plant={plant}
          targetStatus={transitionTarget}
          statusByKey={statusByKey}
          fieldOptions={fieldOptions}
          onSuccess={() => {
            const target = transitionTarget;
            setTransitionTarget(null);
            scrollToStatusAfterRefresh.current = true;
            fetchData();
            const msg = target === 'swapped'
              ? 'Swap recorded. Add or link the received plant in Status Details below.'
              : `Status updated to ${formatStatus(target, statusByKey)}`;
            setToast(msg);
            setTimeout(() => setToast(null), target === 'swapped' ? 5000 : 3000);
          }}
          onCancel={() => setTransitionTarget(null)}
        />
      )}

      {/* -- Confirm ID sheet ---------------------------------------------- */}
      <BottomSheet
        open={confirmSheetOpen}
        onClose={() => setConfirmSheetOpen(false)}
        title="Confirm identification"
        subtitle={`Identified by ${plant.id_source === 'plantnet' ? 'PlantNet' : plant.id_source === 'plantid' ? 'Plant.id' : 'unknown provider'}`}
        maxHeight="75dvh"
      >
        <div className="confirm-id-sheet-body">
          {idTraitsError ? (
            <>
              <p className="confirm-id-error">
                Could not load identification traits. Check your connection and try again.
              </p>
              <div className="confirm-id-footer">
                <button
                  type="button"
                  className="btn btn-primary confirm-id-confirm-btn"
                  onClick={openConfirmSheet}
                >
                  Retry
                </button>
                <button
                  type="button"
                  className="btn btn-secondary confirm-id-confirm-btn"
                  style={{ marginTop: '0.5rem' }}
                  onClick={() => setConfirmSheetOpen(false)}
                >
                  Dismiss
                </button>
              </div>
            </>
          ) : idTraits === null ? (
            <p className="confirm-id-loading">Loading traits…</p>
          ) : idTraits.length === 0 ? (
            <p className="confirm-id-loading">No identification traits available for this plant.</p>
          ) : (
            <>
              <p className="confirm-id-instructions">
                Check the features you have personally verified match this identification.
              </p>
              <ul className="confirm-id-trait-list">
                {idTraits.map(trait => (
                  <li key={trait.key} className="confirm-id-trait-row">
                    <label className="confirm-id-trait-label">
                      <input
                        type="checkbox"
                        className="confirm-id-checkbox"
                        checked={!!traitsChecked[trait.key]}
                        onChange={e => setTraitsChecked(prev => ({ ...prev, [trait.key]: e.target.checked }))}
                      />
                      <span>{trait.label}</span>
                    </label>
                  </li>
                ))}
              </ul>
              <div className="confirm-id-footer">
                <button
                  type="button"
                  className="btn btn-primary confirm-id-confirm-btn"
                  onClick={handleConfirmId}
                  disabled={confirming || !Object.values(traitsChecked).some(Boolean)}
                >
                  {confirming ? 'Confirming…' : 'Confirm identification'}
                </button>
              </div>
            </>
          )}
        </div>
      </BottomSheet>

      {/* -- Plant Info ---------------------------------------------------- */}
      <div className="detail-section">
        <div className="info-grid">
          {plant.acquisition_date && (
            <div className="info-item">
              <span className="info-label">Acquired</span>
              <span className="info-value">{formatDate(plant.acquisition_date)}</span>
            </div>
          )}
          {plant.source && (
            <div className="info-item">
              <span className="info-label">Source</span>
              <span className="info-value">{plant.source}</span>
            </div>
          )}
          {plant.cost && (
            <div className="info-item">
              <span className="info-label">Cost</span>
              <span className="info-value">${plant.cost.toFixed(2)}</span>
            </div>
          )}
          {plant.growing_method && (
            <div className="info-item">
              <span className="info-label">Method</span>
              <span className="info-value">{plant.growing_method}</span>
            </div>
          )}
          {plant.substrate && (
            <div className="info-item">
              <span className="info-label">Substrate</span>
              <span className="info-value">{formatSubstrate(plant.substrate)}</span>
            </div>
          )}
          {plant.moss_pole_type && (
            <div className="info-item">
              <span className="info-label">Moss Pole</span>
              <span className="info-value">{plant.moss_pole_type}</span>
            </div>
          )}
        </div>

        {plant.locations.length > 0 && (
          <div className="detail-tags">
            <span className="info-label">Location</span>
            <div className="tag-list">
              {plant.locations.map(loc => (
                <span key={loc.id} className="detail-tag location-tag">{loc.name}</span>
              ))}
            </div>
          </div>
        )}

        {plant.light_sources.length > 0 && (
          <div className="detail-tags">
            <span className="info-label">Light Source</span>
            <div className="tag-list">
              {plant.light_sources.map(ls => (
                <span key={ls.id} className="detail-tag light-tag">{ls.name}</span>
              ))}
            </div>
          </div>
        )}

        {plant.tags.length > 0 && (
          <div className="detail-tags">
            <span className="info-label">Tags</span>
            <div className="tag-list">
              {plant.tags.map(tag => (
                <span key={tag.id} className="detail-tag">{tag.name}</span>
              ))}
            </div>
          </div>
        )}

        {isWishlistPlant && (
          <div className="detail-section" style={{ marginTop: '0.5rem' }}>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">Wishlist priority</span>
                <span className="info-value">{formatWishlistPriority(plant.wishlist_priority)}</span>
              </div>
              {plant.target_price != null && (
                <div className="info-item">
                  <span className="info-label">Target price</span>
                  <span className="info-value">${plant.target_price.toFixed(2)}</span>
                </div>
              )}
              {plant.shop_name && (
                <div className="info-item">
                  <span className="info-label">Shop</span>
                  <span className="info-value">{plant.shop_name}</span>
                </div>
              )}
            </div>
            {plant.wishlist_url && (
              <div className="detail-tags">
                <span className="info-label">Listing</span>
                <div className="tag-list">
                  <a href={plant.wishlist_url} target="_blank" rel="noreferrer" className="detail-tag">
                    View listing
                  </a>
                </div>
              </div>
            )}
            {plant.acquisition_notes && (
              <div className="detail-tags">
                <span className="info-label">Notes</span>
                <div className="tag-list">
                  <span className="detail-tag">{plant.acquisition_notes}</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {(dispositions.length > 0 || loans.length > 0 || plant.acquisition_disposition) && (
        <div className="detail-section" ref={statusDetailsRef}>
          <h2 className="section-title">Status Details</h2>

          {plant.acquisition_disposition && (
            <div className="status-context-card">
              <div className="status-context-header">
                <span className="status-context-title">Acquired via swap</span>
                {plant.acquisition_disposition.date && (
                  <span className="status-context-date">{formatDate(plant.acquisition_disposition.date)}</span>
                )}
              </div>
              <p className="status-context-line">
                Came from swapping away{' '}
                {plant.acquisition_disposition.plant_id ? (
                  <Link to={`/plants/${plant.acquisition_disposition.plant_id}`}>
                    {plant.acquisition_disposition.outgoing_specimen_id || 'another plant'}
                  </Link>
                ) : 'another plant'}
                {plant.acquisition_disposition.counterparty_name ? ` with ${plant.acquisition_disposition.counterparty_name}` : ''}.
              </p>
              {plant.acquisition_disposition.notes && (
                <p className="status-context-notes">{plant.acquisition_disposition.notes}</p>
              )}
            </div>
          )}

          {dispositions.map(disposition => (
            <div key={disposition.id} className="status-context-card">
              <div className="status-context-header">
                <span className="status-context-title">{formatDispositionType(disposition.type)}</span>
                <div className="status-context-actions">
                  {disposition.date && <span className="status-context-date">{formatDate(disposition.date)}</span>}
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => beginEditDisposition(disposition)}
                    disabled={editingDispositionId === disposition.id}
                  >
                    Edit
                  </button>
                </div>
              </div>
              {editingDispositionId === disposition.id ? renderDispositionEditForm(disposition) : (
                <>
                  <div className="info-grid">
                    {disposition.counterparty_name && (
                      <div className="info-item">
                        <span className="info-label">{disposition.type === 'sold' ? 'Buyer' : disposition.type === 'gifted' ? 'Recipient' : 'Contact'}</span>
                        <span className="info-value">{disposition.counterparty_name}</span>
                      </div>
                    )}
                    {disposition.counterparty_contact && (
                      <div className="info-item">
                        <span className="info-label">Contact</span>
                        <span className="info-value">{disposition.counterparty_contact}</span>
                      </div>
                    )}
                    {formatMoney(disposition.amount) && (
                      <div className="info-item">
                        <span className="info-label">Amount</span>
                        <span className="info-value">{formatMoney(disposition.amount)}</span>
                      </div>
                    )}
                    {disposition.loss_reason && (
                      <div className="info-item">
                        <span className="info-label">Loss reason</span>
                        <span className="info-value">{disposition.loss_reason.replaceAll('_', ' ')}</span>
                      </div>
                    )}
                  </div>
                  {disposition.type === 'swapped' && (
                    <>
                      <p className="status-context-line">
                        Received{' '}
                        {disposition.received_plant_id ? (
                          <Link to={`/plants/${disposition.received_plant_id}`}>
                            {disposition.received_specimen_id || 'linked plant'}
                          </Link>
                        ) : (disposition.received_description || 'unrecorded plant')}
                      </p>
                      {!disposition.received_plant_id && (
                        <div className="status-context-followup">
                          {disposition.received_description && (
                            <p className="status-context-line">You noted: {disposition.received_description}</p>
                          )}
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => addReceivedSwapPlant(disposition)}
                          >
                            Add received plant
                          </button>
                        </div>
                      )}
                    </>
                  )}
                  {disposition.notes && <p className="status-context-notes">{disposition.notes}</p>}
                </>
              )}
            </div>
          ))}

          {loans.map(loan => {
            const todayStr = new Date().toISOString().slice(0, 10);
            const isOverdue = !loan.returned_at && loan.expected_return_at && loan.expected_return_at < todayStr;
            const isExpanded = expandedReturnedLoans.has(loan.id);
            const directionLabel = loan.direction === 'loaned_in' ? 'Loaned In' : 'Loaned Out';
            const counterpartyLabel = loan.direction === 'loaned_in' ? 'from' : 'to';

            if (loan.returned_at) {
              const summary = [
                directionLabel,
                loan.person_name ? `${counterpartyLabel} ${loan.person_name}` : null,
              ].filter(Boolean).join(' ');
              return (
                <div key={loan.id} className="status-context-card loan-history-card">
                  <button
                    type="button"
                    className="loan-history-summary"
                    onClick={() => setExpandedReturnedLoans(prev => {
                      const next = new Set(prev);
                      next.has(loan.id) ? next.delete(loan.id) : next.add(loan.id);
                      return next;
                    })}
                    aria-expanded={isExpanded}
                  >
                    <span className="loan-history-label">
                      <Icon name="check" size={13} />
                      {summary}
                    </span>
                    <span className="loan-history-meta">
                      Returned {formatDate(loan.returned_at)}
                      <Icon name={isExpanded ? 'chevronUp' : 'chevronDown'} size={13} />
                    </span>
                  </button>
                  {isExpanded && (
                    <div className="loan-history-detail">
                      <div className="info-grid">
                        {loan.person_name && (
                          <div className="info-item">
                            <span className="info-label">{loan.direction === 'loaned_in' ? 'From' : 'To'}</span>
                            <span className="info-value">{loan.person_name}</span>
                          </div>
                        )}
                        {loan.person_contact && (
                          <div className="info-item">
                            <span className="info-label">Contact</span>
                            <span className="info-value">{loan.person_contact}</span>
                          </div>
                        )}
                        {loan.loaned_at && (
                          <div className="info-item">
                            <span className="info-label">Loaned</span>
                            <span className="info-value">{formatDate(loan.loaned_at)}</span>
                          </div>
                        )}
                        {loan.expected_return_at && (
                          <div className="info-item">
                            <span className="info-label">Expected return</span>
                            <span className="info-value">{formatDate(loan.expected_return_at)}</span>
                          </div>
                        )}
                      </div>
                      {loan.notes && <p className="status-context-notes">{loan.notes}</p>}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <div key={loan.id} className="status-context-card">
                <div className="status-context-header">
                  <span className="status-context-title">
                    {directionLabel}
                    {isOverdue && (
                      <span className="overdue-badge">Overdue</span>
                    )}
                  </span>
                  <div className="status-context-actions">
                    {loan.loaned_at && <span className="status-context-date">{formatDate(loan.loaned_at)}</span>}
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => beginReturnLoan(loan)}
                      disabled={returningLoanId === loan.id}
                    >
                      Mark as returned
                    </button>
                  </div>
                </div>
                {returningLoanId === loan.id ? renderLoanReturnForm(loan) : (
                  <>
                    <div className="info-grid">
                      {loan.person_name && (
                        <div className="info-item">
                          <span className="info-label">{loan.direction === 'loaned_in' ? 'From' : 'To'}</span>
                          <span className="info-value">{loan.person_name}</span>
                        </div>
                      )}
                      {loan.person_contact && (
                        <div className="info-item">
                          <span className="info-label">Contact</span>
                          <span className="info-value">{loan.person_contact}</span>
                        </div>
                      )}
                      {loan.expected_return_at && (
                        <div className="info-item">
                          <span className="info-label">Expected return</span>
                          <span className="info-value" style={isOverdue ? { color: 'var(--warning, #92400e)', fontWeight: 600 } : {}}>
                            {formatDate(loan.expected_return_at)}
                          </span>
                        </div>
                      )}
                    </div>
                    {loan.notes && <p className="status-context-notes">{loan.notes}</p>}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* -- Species Care Guide -------------------------------------------- */}
      {!isWishlistPlant && (
        <SpeciesCareProfile
          plantId={id}
          plant={plant}
          profile={careProfile}
          loading={careProfileLoading}
          onRequested={() => {}}
          onOpenGuide={() => setCareGuideOpen(true)}
        />
      )}

      {/* -- Care guide sheet ----------------------------------------------- */}
      <BottomSheet
        open={careGuideOpen && !isWishlistPlant}
        onClose={() => setCareGuideOpen(false)}
        maxHeight="92dvh"
        title={plant.common_name
          ? plant.common_name
          : plant.trade_name
            ? plantDisplayName(plant)
            : <em>{plant.genus} {plant.species}</em>}
        ariaLabel={(plant.common_name || plant.trade_name)
          ? `${plantDisplayName(plant)} — ${plant.genus} ${plant.species} care guide`
          : `${plant.genus} ${plant.species} care guide`}
        subtitle={(plant.common_name || plant.trade_name) ? `${plant.genus} ${plant.species}` : undefined}
        actions={
          <Link
            to={`/explore/${encodeURIComponent(plant.genus)}/${encodeURIComponent(plant.species)}`}
            className="care-guide-sheet-explore-link"
            onClick={() => setCareGuideOpen(false)}
          >
            Explore ↗
          </Link>
        }
      >
        <div className="care-guide-body">
          <SpeciesCareProfile
            plantId={id}
            plant={plant}
            profile={careProfile}
            loading={careProfileLoading}
            onRequested={() => {}}
            showFullGuide
            hideIdentitySections
          />
        </div>
      </BottomSheet>

      {canAddCareRecords && (
        <div className="detail-section care-action-section">
          <div className="section-title-row care-action-header">
            <h2 className="section-title" style={{ border: 'none', marginBottom: 0, paddingBottom: 0 }}>
              Care
            </h2>
          </div>
          <div className="care-action-grid">
            <button className="btn btn-primary care-action-btn" onClick={openObsForm}>
              <Icon name="plus" size={15} /> Observation
            </button>
            <button className="btn btn-secondary care-action-btn" onClick={openMeasForm}>
              <Icon name="ruler" size={15} /> Measurement
            </button>
            <button className="btn btn-secondary care-action-btn" onClick={() => setScheduleModalOpen(true)}>
              <Icon name="calendar" size={15} /> Schedule
            </button>
          </div>
        </div>
      )}

      {/* -- Observations -------------------------------------------------- */}
      <div ref={obsFormRef} className="detail-section" style={{ ...(isWishlistPlant ? { display: 'none' } : {}), scrollMarginTop: 'calc(env(safe-area-inset-top, 0px) + 1rem)' }}>
        <div className="section-header-row">
          <button
            type="button"
            className="section-toggle-btn"
            onClick={() => setObservationsOpen(open => !open)}
            aria-expanded={observationsExpanded}
          >
            Observations ({observations.length})
            <span className="collapse-icon">
              <Icon name={observationsExpanded ? 'chevronDown' : 'chevronRight'} size={14} />
            </span>
          </button>
          {canAddCareRecords && (
            <button type="button" className="btn btn-ghost btn-sm section-header-add-btn" onClick={openObsForm}>
              <Icon name="plus" size={13} /> Add
            </button>
          )}
        </div>

        {/* -- Observation Form -------------------------------------------- */}
        {canAddCareRecords && obsFormOpen && (
          <div style={{ position: 'relative' }}>
            <form onSubmit={handleObsSubmit} className="obs-form">
              {obsError && <p className="error">{obsError}</p>}

            <div className="form-grid">
              <div className="form-field">
                <label className="form-label">Event Type</label>
                <select name="event_type" value={eventType} onChange={handleObsChange} className="form-input">
                  {EVENT_TYPES.map(et => (
                    <option key={et.value} value={et.value}>{et.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-field">
                <label className="form-label">Date</label>
                <input type="date" name="date" value={obsForm.date} onChange={handleObsChange} className="form-input" />
              </div>
            </div>

            {/* -- Context-Aware Detail Fields ----------------------------- */}

            {eventType === 'repot' && (
              <div className="form-grid" style={{ marginTop: '0.75rem' }}>
                <div className="form-field">
                  <label className="form-label">Pot Size (inches)</label>
                  <input type="number" name="new_pot_size" value={obsForm.new_pot_size} onChange={handleObsChange} className="form-input" step="0.5" min="0" inputMode="decimal" />
                </div>
                <div className="form-field">
                  <label className="form-label">Pot Type</label>
                  <select name="pot_type" value={obsForm.pot_type} onChange={handleObsChange} className="form-input">
                    <option value="">— Select —</option>
                    {potTypeOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <GrowingMethodSubstrateSelect
                  methods={growingMethods}
                  methodValue={obsForm.new_growing_method_repot}
                  substrateValue={obsForm.new_substrate_repot}
                  onMethodChange={v => setObsForm(p => ({ ...p, new_growing_method_repot: v, new_substrate_repot: '' }))}
                  onSubstrateChange={v => setObsForm(p => ({ ...p, new_substrate_repot: v }))}
                  methodLabel="New Growing Method"
                  substrateLabel="New Substrate"
                />
                <div className="form-field">
                  <label className="form-label">Root Condition</label>
                  <select name="root_condition" value={obsForm.root_condition} onChange={handleObsChange} className="form-input">
                    <option value="">— Select —</option>
                    {rootConditionOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {eventType === 'fertilize' && (
              <div className="form-grid" style={{ marginTop: '0.75rem' }}>
                <div className="form-field">
                  <label className="form-label">Product</label>
                  <HistorySuggestInput field="fert_product" name="fert_product" value={obsForm.fert_product} onChange={handleObsChange} placeholder="e.g. Superthrive" />
                </div>
                <div className="form-field">
                  <label className="form-label">Concentration</label>
                  <HistorySuggestInput field="fert_concentration" name="concentration" value={obsForm.concentration} onChange={handleObsChange} placeholder="e.g. 1/4 strength" />
                </div>
                <div className="form-field">
                  <label className="form-label">Method</label>
                  <select name="fert_method" value={obsForm.fert_method} onChange={handleObsChange} className="form-input">
                    <option value="">— Select —</option>
                    {fertilizeMethodOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {eventType === 'treatment' && (
              <div className="form-grid" style={{ marginTop: '0.75rem' }}>
                <div className="form-field">
                  <label className="form-label">Category</label>
                  <select name="treatment_category" value={obsForm.treatment_category} onChange={handleObsChange} className="form-input">
                    <option value="">— Select —</option>
                    {treatmentCategoryOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-field">
                  <label className="form-label">Target</label>
                  <HistorySuggestInput field="treatment_target" name="target" value={obsForm.target} onChange={handleObsChange} placeholder="e.g. Thrips, Spider Mites" />
                </div>
                <div className="form-field">
                  <label className="form-label">Product</label>
                  <HistorySuggestInput field="treatment_product" name="treat_product" value={obsForm.treat_product} onChange={handleObsChange} placeholder="e.g. Neem oil" />
                </div>
                <div className="form-field">
                  <label className="form-label">Method</label>
                  <select name="treat_method" value={obsForm.treat_method} onChange={handleObsChange} className="form-input">
                    <option value="">— Select —</option>
                    {treatmentMethodOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {eventType === 'propagation' && (
              <div style={{ marginTop: '0.75rem' }}>

                {/* Reproduction type pills */}
                <div style={{ marginBottom: '0.75rem' }}>
                  <label className="form-label">Reproduction Type</label>
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.35rem' }}>
                    {[['clone', 'Clone'], ['seed', 'Seed']].map(([val, lbl]) => (
                      <button
                        key={val}
                        type="button"
                        onClick={() => setObsForm(prev => ({
                          ...prev,
                          prop_repro_type: val,
                          prop_method: val === 'clone' ? (prev.prop_method || 'cutting') : prev.prop_method,
                          prop_pollination_type: val === 'seed' ? (prev.prop_pollination_type || 'self') : prev.prop_pollination_type,
                        }))}
                        style={{
                          padding: '0.4rem 1rem',
                          borderRadius: 20,
                          border: `2px solid ${obsForm.prop_repro_type === val ? 'var(--green, #3a7d44)' : 'var(--border, #ddd)'}`,
                          background: obsForm.prop_repro_type === val ? 'var(--green-light, #f0f7f1)' : 'var(--surface, #fff)',
                          color: obsForm.prop_repro_type === val ? 'var(--green, #3a7d44)' : 'var(--text, #333)',
                          fontWeight: obsForm.prop_repro_type === val ? 600 : 400,
                          cursor: 'pointer',
                          fontSize: '0.88rem',
                        }}
                      >
                        {lbl}
                      </button>
                    ))}
                  </div>
                </div>

                {obsForm.prop_repro_type === 'seed' && (
                  <div className="form-grid" style={{ marginBottom: '0.75rem' }}>
                    <div className="form-field">
                      <label className="form-label">Pollination Type</label>
                      <select
                        name="prop_pollination_type"
                        value={obsForm.prop_pollination_type}
                        onChange={handleObsChange}
                        className="form-input"
                      >
                        {POLLINATION_TYPES.map(([value, label]) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* Seed cross: second parent selector */}
                {obsForm.prop_repro_type === 'seed' && obsForm.prop_pollination_type === 'cross' && (
                  <div style={{
                    marginBottom: '0.75rem', padding: '0.75rem',
                    background: 'var(--purple-light, #f5f0fa)', borderRadius: 8,
                    border: '1px solid var(--purple-border, #d8c8f0)',
                  }}>
                    <p style={{ margin: '0 0 0.5rem', fontWeight: 600, color: 'var(--purple, #6a2d8a)', fontSize: '0.88rem' }}>
                      Cross-pollinated seed
                    </p>
                    <label className="form-label">Search for the other parent plant</label>
                    <div style={{ position: 'relative', marginTop: '0.35rem' }}>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Search by name or specimen ID…"
                        value={parentSearchQuery}
                        onFocus={() => loadSecondParentOptions(parentSearchQuery)}
                        onChange={e => searchSecondParent(e.target.value)}
                      />
                      {parentSearchResults.length > 0 && (
                        <div style={{
                          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                          background: 'var(--surface, #fff)', border: '1px solid var(--border, #ddd)',
                          borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: 200, overflowY: 'auto',
                        }}>
                          {parentSearchResults.map(p => (
                            <div
                              key={p.id}
                              onClick={() => {
                                setObsForm(prev => ({ ...prev, prop_second_parent: p }));
                                setParentSearchQuery(formatPlantSearchLabel(p));
                                setParentSearchResults([]);
                              }}
                              style={{
                                padding: '0.5rem 0.75rem', cursor: 'pointer', fontSize: '0.85rem',
                                borderBottom: '1px solid var(--border, #eee)',
                              }}
                              onMouseEnter={e => { e.currentTarget.style.background = 'var(--surface-hover, #f5f5f5)'; }}
                              onMouseLeave={e => { e.currentTarget.style.background = ''; }}
                            >
                              <strong>{p.specimen_id}</strong>
                              <span style={{ marginLeft: '0.4rem', fontStyle: p.species ? 'italic' : 'normal' }}>
                                {[p.genus, p.species].filter(Boolean).join(' ')}
                                {p.trade_name ? `${p.species ? ' ' : ''}${p.trade_name}` : ''}
                              </span>
                              {p.common_name && (
                                <span style={{ marginLeft: '0.4rem', color: 'var(--text-light, #888)' }}>({p.common_name})</span>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    {obsForm.prop_second_parent && (
                      <div style={{ marginTop: '0.65rem' }}>
                        <label className="form-label">Which plant is the seed parent (♀)?</label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', marginTop: '0.35rem' }}>
                          <label className="checkbox-label">
                            <input
                              type="radio"
                              name="seed_parent"
                              checked={obsForm.prop_mother_is_current}
                              onChange={() => setObsForm(prev => ({ ...prev, prop_mother_is_current: true }))}
                            />
                            <span>
                              <strong>{plant?.specimen_id}</strong> ♀ seed parent
                              {' · '}
                              <strong>{obsForm.prop_second_parent?.specimen_id}</strong> ♂ pollen donor
                            </span>
                          </label>
                          <label className="checkbox-label">
                            <input
                              type="radio"
                              name="seed_parent"
                              checked={!obsForm.prop_mother_is_current}
                              onChange={() => setObsForm(prev => ({ ...prev, prop_mother_is_current: false }))}
                            />
                            <span>
                              <strong>{obsForm.prop_second_parent?.specimen_id}</strong> ♀ seed parent
                              {' · '}
                              <strong>{plant?.specimen_id}</strong> ♂ pollen donor
                            </span>
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Clone: method (primary) → rooting hormone → wound treatment */}
                {obsForm.prop_repro_type === 'clone' && (
                  <>
                    {/* Step 1: Pick the method */}
                    <div className="form-field" style={{ marginBottom: '0.75rem' }}>
                      <label className="form-label">Propagation Method</label>
                      <select name="prop_method" value={obsForm.prop_method} onChange={handleObsChange} className="form-input">
                        {CLONE_METHODS.map(([value, label]) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                    </div>

                    {/* Step 2: Rooting hormone + optional concentration (revealed after method) */}
                    {propHormoneOptions.length > 0 && (
                      <div className="form-grid" style={{ marginBottom: '0.75rem' }}>
                        <div className="form-field">
                          <label className="form-label">Rooting Hormone</label>
                          <select
                            name="prop_rooting_hormone"
                            value={obsForm.prop_rooting_hormone}
                            onChange={handleObsChange}
                            className="form-input"
                          >
                            <option value="">— None —</option>
                            {propHormoneOptions
                              .filter(o => o.value.toLowerCase() !== 'none')
                              .map(o => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                          </select>
                        </div>
                        {obsForm.prop_rooting_hormone && (
                          <div className="form-field">
                            <label className="form-label">Concentration / Notes</label>
                            <input
                              type="text"
                              name="prop_hormone_concentration"
                              value={obsForm.prop_hormone_concentration}
                              onChange={handleObsChange}
                              className="form-input"
                              placeholder="e.g. Diluted 1:4, quick dip"
                            />
                          </div>
                        )}
                      </div>
                    )}

                    {/* Step 3: Wound treatment (only for cut-type methods) */}
                    {WOUND_TREATMENT_METHODS.has(obsForm.prop_method) && (
                      <div style={{ marginBottom: '0.75rem' }}>
                        <label className="form-label">Wound Treatment</label>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.35rem' }}>
                          {[['callused', 'Callused'], ['cinnamon', 'Cinnamon'], ['h2o2', 'H₂O₂ rinse'], ['charcoal', 'Activated charcoal']].map(([val, lbl]) => (
                            <label key={val} className="checkbox-label">
                              <input
                                type="checkbox"
                                checked={(obsForm.prop_wound_treatment || []).includes(val)}
                                onChange={e => setObsForm(prev => ({
                                  ...prev,
                                  prop_wound_treatment: e.target.checked
                                    ? [...(prev.prop_wound_treatment || []), val]
                                    : (prev.prop_wound_treatment || []).filter(x => x !== val),
                                }))}
                              />
                              {lbl}
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Single plant vs batch mode toggle (clone only) */}
                {obsForm.prop_repro_type === 'clone' && (
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label className="form-label">Outcome</label>
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.35rem' }}>
                      {[
                        { key: false, label: 'Single plant' },
                        { key: true,  label: 'Batch of items' },
                      ].map(({ key, label }) => (
                        <button
                          key={String(key)}
                          type="button"
                          onClick={() => setObsForm(prev => ({ ...prev, prop_batch_mode: key, prop_create_child: !key }))}
                          style={{
                            padding: '0.4rem 1rem', borderRadius: 20, cursor: 'pointer', fontSize: '0.88rem',
                            border: `2px solid ${obsForm.prop_batch_mode === key ? 'var(--green, #3a7d44)' : 'var(--border, #ddd)'}`,
                            background: obsForm.prop_batch_mode === key ? 'var(--green-light, #f0f7f1)' : 'var(--surface, #fff)',
                            color: obsForm.prop_batch_mode === key ? 'var(--green, #3a7d44)' : 'var(--text, #333)',
                            fontWeight: obsForm.prop_batch_mode === key ? 600 : 400,
                          }}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {obsForm.prop_batch_mode ? (
                  /* Batch mode: explain the post-save workflow */
                  <div style={{
                    padding: '0.65rem 0.85rem', borderRadius: 8, marginBottom: '0.65rem',
                    background: 'var(--surface-recessed, #f5f5f5)', border: '1px solid var(--border, #ddd)',
                  }}>
                    <p style={{ margin: '0 0 0.2rem', fontWeight: 600, fontSize: '0.85rem' }}>
                      Batch tracking
                    </p>
                    <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--text-light, #666)' }}>
                      After saving, use the <strong>+ Batch</strong> button on this observation to add
                      individual items (corms, cuttings, etc.) and track each one through to graduation.
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Auto-create hint card */}
                    <div style={{
                      padding: '0.65rem 0.85rem', borderRadius: 8, marginBottom: '0.65rem',
                      background: 'var(--green-light, #f0f7f1)', border: '1px solid var(--green-border, #c3dfc9)',
                    }}>
                      <p style={{ margin: '0 0 0.2rem', fontWeight: 600, color: 'var(--green, #3a7d44)', fontSize: '0.85rem' }}>
                        A new child plant will be created automatically
                      </p>
                      <p style={{ margin: 0, fontSize: '0.82rem', color: 'var(--text, #444)' }}>
                        {'Parent: '}
                        <strong>{plantDisplayName(plant)}</strong>
                        {' (' + (plant?.specimen_id || '') + ') → New: '}
                        <strong>
                          {(() => {
                            if (obsForm.prop_override_name && obsForm.child_genus) {
                              return (obsForm.child_genus + ' ' + (obsForm.child_species || '')).trim();
                            }
                            if (obsForm.prop_repro_type === 'seed' && obsForm.prop_pollination_type === 'cross' && obsForm.prop_second_parent) {
                              const seed  = obsForm.prop_mother_is_current ? plant : obsForm.prop_second_parent;
                              const pollen = obsForm.prop_mother_is_current ? obsForm.prop_second_parent : plant;
                              const strip = (sp) => sp ? sp.replace(/\s*'[^']*'/g, '').replace(/\s+(subsp|var|f)\.\s+\S+/g, '').trim() : '';
                              const intergeneric = seed?.genus?.toLowerCase() !== pollen?.genus?.toLowerCase();
                              return intergeneric
                                ? (seed?.genus + ' × ' + pollen?.genus + ' ' + strip(seed?.species))
                                : (seed?.genus + ' ' + strip(seed?.species) + ' × ' + strip(pollen?.species));
                            }
                            return plant?.genus + ' ' + plant?.species;
                          })()}
                        </strong>
                      </p>
                    </div>

                    {/* Child status */}
                    <div className="form-grid" style={{ marginBottom: '0.5rem' }}>
                      <div className="form-field">
                        <label className="form-label">Child Status</label>
                        <select name="child_status" value={obsForm.child_status} onChange={handleObsChange} className="form-input">
                          {statusMetadata.filter(status => status.include_in_collection).map(status => (
                            <option key={status.key} value={status.key}>{formatStatus(status.key, statusByKey)}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Name override */}
                    <label className="checkbox-label" style={{ marginBottom: '0.35rem' }}>
                      <input
                        type="checkbox"
                        checked={obsForm.prop_override_name}
                        onChange={e => {
                          if (e.target.checked) {
                            let prefillGenus = plant?.genus || '';
                            let prefillSpecies = plant?.species || '';
                            if (obsForm.prop_repro_type === 'seed' && obsForm.prop_pollination_type === 'cross' && obsForm.prop_second_parent) {
                              const seed   = obsForm.prop_mother_is_current ? plant : obsForm.prop_second_parent;
                              const pollen = obsForm.prop_mother_is_current ? obsForm.prop_second_parent : plant;
                              const intergeneric = seed?.genus?.toLowerCase() !== pollen?.genus?.toLowerCase();
                              prefillGenus   = intergeneric ? (seed?.genus + ' × ' + pollen?.genus) : seed?.genus;
                              const stripSp = (sp) => sp ? sp.replace(/\s*'[^']*'/g, '').replace(/\s+(subsp|var|f)\.\s+\S+/g, '').trim() : '';
                              prefillSpecies = intergeneric
                                ? stripSp(seed?.species)
                                : (stripSp(seed?.species) + ' × ' + stripSp(pollen?.species));
                            }
                            setObsForm(prev => ({
                              ...prev,
                              prop_override_name: true,
                              child_genus: prev.child_genus || prefillGenus,
                              child_species: prev.child_species || prefillSpecies,
                            }));
                          } else {
                            setObsForm(prev => ({ ...prev, prop_override_name: false }));
                          }
                        }}
                      />
                      Override child plant name
                    </label>

                    {obsForm.prop_override_name && (
                      <div className="form-grid" style={{ marginBottom: '0.5rem' }}>
                        <div className="form-field">
                          <label className="form-label">Genus</label>
                          <input type="text" name="child_genus" value={obsForm.child_genus}
                            onChange={handleObsChange} className="form-input" placeholder={plant?.genus} />
                        </div>
                        <div className="form-field">
                          <label className="form-label">Species</label>
                          <input type="text" name="child_species" value={obsForm.child_species}
                            onChange={handleObsChange} className="form-input" placeholder={plant?.species} />
                        </div>
                        <div className="form-field">
                          <label className="form-label">Common Name</label>
                          <input type="text" name="child_common_name" value={obsForm.child_common_name}
                            onChange={handleObsChange} className="form-input" placeholder="Leave blank to omit" />
                        </div>
                      </div>
                    )}

                    {/* Opt-out */}
                    <label className="checkbox-label" style={{ marginTop: '0.25rem' }}>
                      <input
                        type="checkbox"
                        checked={!obsForm.prop_create_child}
                        onChange={e => setObsForm(prev => ({ ...prev, prop_create_child: !e.target.checked }))}
                      />
                      {"Don't create a child plant record"}
                    </label>
                  </>
                )}

              </div>
            )}

            {eventType === 'substrate_change' && (
              <div className="form-grid" style={{ marginTop: '0.75rem' }}>
                <GrowingMethodSubstrateSelect
                  methods={growingMethods}
                  methodValue={obsForm.old_growing_method}
                  substrateValue={obsForm.old_substrate}
                  onMethodChange={v => setObsForm(p => ({ ...p, old_growing_method: v, old_substrate: '' }))}
                  onSubstrateChange={v => setObsForm(p => ({ ...p, old_substrate: v }))}
                  methodLabel="Old Growing Method"
                  substrateLabel="Old Substrate"
                />
                <GrowingMethodSubstrateSelect
                  methods={growingMethods}
                  methodValue={obsForm.new_growing_method}
                  substrateValue={obsForm.new_substrate}
                  onMethodChange={v => setObsForm(p => ({ ...p, new_growing_method: v, new_substrate: '' }))}
                  onSubstrateChange={v => setObsForm(p => ({ ...p, new_substrate: v }))}
                  methodLabel="New Growing Method"
                  substrateLabel="New Substrate"
                />
                <div className="form-field">
                  <label className="form-label">Reason</label>
                  <HistorySuggestInput field="substrate_change_reason" name="change_reason" value={obsForm.change_reason} onChange={handleObsChange} placeholder="e.g. Experiment, Root issues" />
                </div>
              </div>
            )}

            {eventType === 'acclimation' && (
              <div className="form-grid" style={{ marginTop: '0.75rem' }}>
                <div className="form-field">
                  <label className="form-label">Method</label>
                  <HistorySuggestInput field="acclimation_method" name="accl_method" value={obsForm.accl_method} onChange={handleObsChange} placeholder="e.g. Humidity dome" />
                </div>
                <div className="form-field">
                  <label className="form-label">Source Condition</label>
                  <HistorySuggestInput field="source_condition" name="source_condition" value={obsForm.source_condition} onChange={handleObsChange} placeholder="e.g. Shipped bare root" />
                </div>
                <div className="form-field">
                  <label className="form-label">Start Date</label>
                  <input type="date" name="start_date" value={obsForm.start_date} onChange={handleObsChange} className="form-input" />
                </div>
                <div className="form-field">
                  <label className="form-label">End Date</label>
                  <input type="date" name="end_date" value={obsForm.end_date} onChange={handleObsChange} className="form-input" />
                </div>
              </div>
            )}

            {/* Notes — always visible */}
            <div className="form-field" style={{ marginTop: '0.75rem' }}>
              <label className="form-label">Notes</label>
              <textarea name="notes" value={obsForm.notes} onChange={handleObsChange} className="form-input form-textarea" rows="2" placeholder="Optional notes..." />
            </div>

            {/* Photo attachment */}
            <div className="form-field" style={{ marginTop: '0.75rem' }}>
              <label className="form-label">Attach Photo</label>
              {eventType === 'propagation' && obsForm.prop_create_child && (
                <p style={{ margin: '0 0 0.35rem', fontSize: '0.82rem', color: 'var(--text-light, #777)' }}>
                  Propagation photos will be saved to the child plant.
                </p>
              )}
              <div className="photo-upload-controls">
                <PhotoLabelField value={obsPhotoPart} onChange={setObsPhotoPart} />
                <div className="photo-attach-row">
                <label className="btn btn-secondary btn-sm photo-attach-btn">
                  <Icon name="camera" size={14} style={{marginRight:4}} />Camera
                  <input type="file" accept="image/*" capture="environment" onChange={e => setObsPhotoFile(e.target.files?.[0] || null)} style={{ display: 'none' }} />
                </label>
                <label className="btn btn-secondary btn-sm photo-attach-btn">
                  <Icon name="image" size={14} style={{marginRight:4}} />Gallery
                  <input type="file" accept="image/*" onChange={e => setObsPhotoFile(e.target.files?.[0] || null)} style={{ display: 'none' }} />
                </label>
                {obsPhotoFile && (
                  <div className="photo-attach-preview">
                    <img src={URL.createObjectURL(obsPhotoFile)} alt="Preview" className="photo-attach-thumb" />
                    <button type="button" className="btn-icon" onClick={() => { setObsPhotoFile(null); setObsPhotoPart(''); }} title="Remove"><Icon name="x" size={14} /></button>
                  </div>
                )}
                </div>
              </div>
            </div>

            <div className="form-actions" style={{ marginTop: '0.75rem', marginBottom: 0 }}>
              <div></div>
              <div className="form-actions-right">
                <button type="button" className="btn btn-secondary btn-sm" onClick={resetObsForm}>Cancel</button>
                <button type="submit" className="btn btn-primary btn-sm" disabled={obsSaving}>
                  {obsSaving ? 'Saving...' : (
                    eventType === 'propagation' && obsForm.prop_batch_mode
                      ? 'Log Propagation'
                      : eventType === 'propagation' && obsForm.prop_create_child
                        ? 'Log & Create Child Plant'
                        : 'Log Observation'
                  )}
                </button>
              </div>
            </div>
            </form>
            <LoadingOverlay
              isLoading={obsSaving}
              message="Saving observation..."
            />
          </div>
        )}

        {/* -- Observation Timeline ---------------------------------------- */}
        {observations.length === 0 && !obsFormOpen ? (
          <p className="empty-state">No observations yet</p>
        ) : (
          <div className="timeline" style={{ marginTop: obsFormOpen ? '1rem' : 0 }}>
            {visibleObservations.map(obs => (
              editingObsId === obs.id ? (
                <div key={obs.id} className="timeline-item timeline-item-editing">
                  <div className="timeline-header">
                    <span className={`event-badge event-${obs.event_type}`}>
                      {obs.event_type.replace('_', ' ')}
                    </span>
                    <span className="edit-locked-note">Event type cannot be changed</span>
                  </div>

                  <div className="form-grid" style={{ marginTop: '0.5rem' }}>
                    <div className="form-field">
                      <label className="form-label">Date</label>
                      <input type="date" name="date" value={editObsForm.date} onChange={handleEditObsChange} className="form-input" />
                    </div>
                  </div>

                  {editObsForm.event_type === 'repot' && (
                    <div className="form-grid" style={{ marginTop: '0.5rem' }}>
                      <div className="form-field">
                        <label className="form-label">Pot Size (inches)</label>
                        <input type="number" name="new_pot_size" value={editObsForm.new_pot_size} onChange={handleEditObsChange} className="form-input" step="0.5" min="0" inputMode="decimal" />
                      </div>
                      <div className="form-field">
                        <label className="form-label">Pot Type</label>
                        <select name="pot_type" value={editObsForm.pot_type} onChange={handleEditObsChange} className="form-input">
                          <option value="">— Select —</option>
                          {potTypeOptions.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </div>
                      <GrowingMethodSubstrateSelect
                        methods={growingMethods}
                        methodValue={editObsForm.new_growing_method_repot}
                        substrateValue={editObsForm.new_substrate_repot}
                        onMethodChange={v => setEditObsForm(p => ({ ...p, new_growing_method_repot: v, new_substrate_repot: '' }))}
                        onSubstrateChange={v => setEditObsForm(p => ({ ...p, new_substrate_repot: v }))}
                        methodLabel="New Growing Method"
                        substrateLabel="New Substrate"
                      />
                      <div className="form-field">
                        <label className="form-label">Root Condition</label>
                        <select name="root_condition" value={editObsForm.root_condition} onChange={handleEditObsChange} className="form-input">
                          <option value="">— Select —</option>
                          {rootConditionOptions.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {editObsForm.event_type === 'fertilize' && (
                    <div className="form-grid" style={{ marginTop: '0.5rem' }}>
                      <div className="form-field">
                        <label className="form-label">Product</label>
                        <HistorySuggestInput field="fert_product" name="fert_product" value={editObsForm.fert_product} onChange={handleEditObsChange} />
                      </div>
                      <div className="form-field">
                        <label className="form-label">Concentration</label>
                        <HistorySuggestInput field="fert_concentration" name="concentration" value={editObsForm.concentration} onChange={handleEditObsChange} />
                      </div>
                      <div className="form-field">
                        <label className="form-label">Method</label>
                        <select name="fert_method" value={editObsForm.fert_method} onChange={handleEditObsChange} className="form-input">
                          <option value="">— Select —</option>
                          {fertilizeMethodOptions.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {editObsForm.event_type === 'treatment' && (
                    <div className="form-grid" style={{ marginTop: '0.5rem' }}>
                      <div className="form-field">
                        <label className="form-label">Category</label>
                        <select name="treatment_category" value={editObsForm.treatment_category} onChange={handleEditObsChange} className="form-input">
                          <option value="">— Select —</option>
                          {treatmentCategoryOptions.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </div>
                      <div className="form-field">
                        <label className="form-label">Target</label>
                        <HistorySuggestInput field="treatment_target" name="target" value={editObsForm.target} onChange={handleEditObsChange} />
                      </div>
                      <div className="form-field">
                        <label className="form-label">Product</label>
                        <HistorySuggestInput field="treatment_product" name="treat_product" value={editObsForm.treat_product} onChange={handleEditObsChange} />
                      </div>
                      <div className="form-field">
                        <label className="form-label">Method</label>
                        <select name="treat_method" value={editObsForm.treat_method} onChange={handleEditObsChange} className="form-input">
                          <option value="">— Select —</option>
                          {treatmentMethodOptions.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {editObsForm.event_type === 'propagation' && (
                    <>
                      <div className="form-grid" style={{ marginTop: '0.5rem' }}>
                        <div className="form-field">
                          <label className="form-label">Category</label>
                          <select name="prop_repro_type" value={editObsForm.prop_repro_type} onChange={handleEditObsChange} className="form-input">
                            <option value="clone">Clone</option>
                            <option value="seed">Seed</option>
                          </select>
                        </div>
                        {editObsForm.prop_repro_type === 'clone' ? (
                          <div className="form-field">
                            <label className="form-label">Propagation Method</label>
                            <select name="prop_method" value={editObsForm.prop_method} onChange={handleEditObsChange} className="form-input">
                              {CLONE_METHODS.map(([value, label]) => (
                                <option key={value} value={value}>{label}</option>
                              ))}
                            </select>
                          </div>
                        ) : (
                          <div className="form-field">
                            <label className="form-label">Pollination Type</label>
                            <select name="prop_pollination_type" value={editObsForm.prop_pollination_type} onChange={handleEditObsChange} className="form-input">
                              {POLLINATION_TYPES.map(([value, label]) => (
                                <option key={value} value={value}>{label}</option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>

                      {/* Rooting hormone + wound treatment (clone only) */}
                      {editObsForm.prop_repro_type === 'clone' && (
                        <>
                          {propHormoneOptions.length > 0 && (
                            <div className="form-grid" style={{ marginTop: '0.5rem' }}>
                              <div className="form-field">
                                <label className="form-label">Rooting Hormone</label>
                                <select
                                  name="prop_rooting_hormone"
                                  value={editObsForm.prop_rooting_hormone}
                                  onChange={handleEditObsChange}
                                  className="form-input"
                                >
                                  <option value="">— None —</option>
                                  {propHormoneOptions
                                    .filter(o => o.value.toLowerCase() !== 'none')
                                    .map(o => (
                                      <option key={o.value} value={o.value}>{o.label}</option>
                                    ))}
                                </select>
                              </div>
                              {editObsForm.prop_rooting_hormone && (
                                <div className="form-field">
                                  <label className="form-label">Concentration / Notes</label>
                                  <input
                                    type="text"
                                    name="prop_hormone_concentration"
                                    value={editObsForm.prop_hormone_concentration}
                                    onChange={handleEditObsChange}
                                    className="form-input"
                                    placeholder="e.g. Diluted 1:4, quick dip"
                                  />
                                </div>
                              )}
                            </div>
                          )}
                          {WOUND_TREATMENT_METHODS.has(editObsForm.prop_method) && (
                            <div style={{ marginTop: '0.5rem' }}>
                              <label className="form-label">Wound Treatment</label>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.35rem' }}>
                                {[['callused', 'Callused'], ['cinnamon', 'Cinnamon'], ['h2o2', 'H₂O₂ rinse'], ['charcoal', 'Activated charcoal']].map(([val, lbl]) => (
                                  <label key={val} className="checkbox-label">
                                    <input
                                      type="checkbox"
                                      checked={(editObsForm.prop_wound_treatment || []).includes(val)}
                                      onChange={e => setEditObsForm(prev => ({
                                        ...prev,
                                        prop_wound_treatment: e.target.checked
                                          ? [...(prev.prop_wound_treatment || []), val]
                                          : (prev.prop_wound_treatment || []).filter(x => x !== val),
                                      }))}
                                    />
                                    {lbl}
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      {editObsForm.prop_repro_type === 'seed' && editObsForm.prop_pollination_type === 'cross' && (
                        <div style={{
                          marginTop: '0.5rem', padding: '0.75rem',
                          background: 'var(--purple-light, #f5f0fa)', borderRadius: 8,
                          border: '1px solid var(--purple-border, #d8c8f0)',
                        }}>
                          <label className="form-label">Other parent plant</label>
                          <div style={{ position: 'relative', marginTop: '0.35rem' }}>
                            <input
                              type="text"
                              className="form-input"
                              placeholder="Search by name or specimen ID..."
                              value={editParentSearchQuery}
                              onFocus={() => loadEditSecondParentOptions(editParentSearchQuery)}
                              onChange={e => searchEditSecondParent(e.target.value)}
                            />
                            {editParentSearchResults.length > 0 && (
                              <div style={{
                                position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
                                background: 'var(--surface, #fff)', border: '1px solid var(--border, #ddd)',
                                borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', maxHeight: 200, overflowY: 'auto',
                              }}>
                                {editParentSearchResults.map(p => (
                                  <div
                                    key={p.id}
                                    onClick={() => {
                                      setEditObsForm(prev => ({ ...prev, prop_second_parent: p }));
                                      setEditParentSearchQuery(formatPlantSearchLabel(p));
                                      setEditParentSearchResults([]);
                                    }}
                                    style={{
                                      padding: '0.5rem 0.75rem', cursor: 'pointer', fontSize: '0.85rem',
                                      borderBottom: '1px solid var(--border, #eee)',
                                    }}
                                  >
                                    <strong>{p.specimen_id}</strong>
                                    <span style={{ marginLeft: '0.4rem', fontStyle: p.species ? 'italic' : 'normal' }}>
                                      {[p.genus, p.species].filter(Boolean).join(' ')}
                                      {p.trade_name ? `${p.species ? ' ' : ''}${p.trade_name}` : ''}
                                    </span>
                                    {p.common_name && (
                                      <span style={{ marginLeft: '0.4rem', color: 'var(--text-light, #888)' }}>({p.common_name})</span>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          {editObsForm.prop_second_parent && (
                            <div style={{ marginTop: '0.65rem' }}>
                              <label className="form-label">Which plant is the seed parent (♀)?</label>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', marginTop: '0.35rem' }}>
                                <label className="checkbox-label">
                                  <input
                                    type="radio"
                                    name="edit_seed_parent"
                                    checked={editObsForm.prop_mother_is_current}
                                    onChange={() => setEditObsForm(prev => ({ ...prev, prop_mother_is_current: true }))}
                                  />
                                  <span>
                                    <strong>{plant?.specimen_id}</strong> ♀ seed parent
                                    {' · '}
                                    <strong>{editObsForm.prop_second_parent?.specimen_id}</strong> ♂ pollen donor
                                  </span>
                                </label>
                                <label className="checkbox-label">
                                  <input
                                    type="radio"
                                    name="edit_seed_parent"
                                    checked={!editObsForm.prop_mother_is_current}
                                    onChange={() => setEditObsForm(prev => ({ ...prev, prop_mother_is_current: false }))}
                                  />
                                  <span>
                                    <strong>{editObsForm.prop_second_parent?.specimen_id}</strong> ♀ seed parent
                                    {' · '}
                                    <strong>{plant?.specimen_id}</strong> ♂ pollen donor
                                  </span>
                                </label>
                              </div>
                              {editObsForm.has_child_plant && (
                                <p style={{ margin: '0.5rem 0 0', fontSize: '0.82rem', color: 'var(--text-light, #777)' }}>
                                  Parent changes update the mirrored propagation record but do not rename the linked child plant.
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {editObsForm.event_type === 'substrate_change' && (
                    <div className="form-grid" style={{ marginTop: '0.5rem' }}>
                      <GrowingMethodSubstrateSelect
                        methods={growingMethods}
                        methodValue={editObsForm.old_growing_method}
                        substrateValue={editObsForm.old_substrate}
                        onMethodChange={v => setEditObsForm(p => ({ ...p, old_growing_method: v, old_substrate: '' }))}
                        onSubstrateChange={v => setEditObsForm(p => ({ ...p, old_substrate: v }))}
                        methodLabel="Old Growing Method"
                        substrateLabel="Old Substrate"
                      />
                      <GrowingMethodSubstrateSelect
                        methods={growingMethods}
                        methodValue={editObsForm.new_growing_method}
                        substrateValue={editObsForm.new_substrate}
                        onMethodChange={v => setEditObsForm(p => ({ ...p, new_growing_method: v, new_substrate: '' }))}
                        onSubstrateChange={v => setEditObsForm(p => ({ ...p, new_substrate: v }))}
                        methodLabel="New Growing Method"
                        substrateLabel="New Substrate"
                      />
                      <div className="form-field">
                        <label className="form-label">Reason</label>
                        <HistorySuggestInput field="substrate_change_reason" name="change_reason" value={editObsForm.change_reason} onChange={handleEditObsChange} />
                      </div>
                    </div>
                  )}

                  {editObsForm.event_type === 'acclimation' && (
                    <div className="form-grid" style={{ marginTop: '0.5rem' }}>
                      <div className="form-field">
                        <label className="form-label">Method</label>
                        <HistorySuggestInput field="acclimation_method" name="accl_method" value={editObsForm.accl_method} onChange={handleEditObsChange} />
                      </div>
                      <div className="form-field">
                        <label className="form-label">Source Condition</label>
                        <HistorySuggestInput field="source_condition" name="source_condition" value={editObsForm.source_condition} onChange={handleEditObsChange} />
                      </div>
                      <div className="form-field">
                        <label className="form-label">Start Date</label>
                        <input type="date" name="start_date" value={editObsForm.start_date} onChange={handleEditObsChange} className="form-input" />
                      </div>
                      <div className="form-field">
                        <label className="form-label">End Date</label>
                        <input type="date" name="end_date" value={editObsForm.end_date} onChange={handleEditObsChange} className="form-input" />
                      </div>
                    </div>
                  )}

                  <div className="form-field" style={{ marginTop: '0.5rem' }}>
                    <label className="form-label">Notes</label>
                    <textarea name="notes" value={editObsForm.notes} onChange={handleEditObsChange} className="form-input form-textarea" rows="2" />
                  </div>

                  {/* Existing linked photos */}
                  {photos.filter(p => p.observation_id === obs.id).length > 0 && (
                    <div className="linked-photos" style={{ marginTop: '0.5rem' }}>
                      <span className="form-label">Linked Photos</span>
                      <div className="photo-attach-row" style={{ marginTop: '0.25rem' }}>
                        {photos.filter(p => p.observation_id === obs.id).map(p => (
                          <div key={p.id} className="photo-attach-preview">
                            <img src={photoUrl(p.id, p.rotation_version, p.created_at)} alt="" className="photo-attach-thumb" onClick={() => setLightboxPhoto(p)} />
                            <button type="button" className="btn-icon" onClick={() => handleDeletePhoto(p.id)} title="Remove"><Icon name="trash" size={14} /></button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Add new photo */}
	                  <div className="form-field" style={{ marginTop: '0.5rem' }}>
	                    <label className="form-label">Attach Photo</label>
	                    <div className="photo-upload-controls">
	                      <PhotoLabelField value={editObsPhotoPart} onChange={setEditObsPhotoPart} />
	                      <div className="photo-attach-row">
	                      <label className="btn btn-secondary btn-sm photo-attach-btn">
                        <Icon name="camera" size={14} style={{marginRight:4}} />Camera
                        <input type="file" accept="image/*" capture="environment" onChange={e => setEditObsPhotoFile(e.target.files?.[0] || null)} style={{ display: 'none' }} />
                      </label>
                      <label className="btn btn-secondary btn-sm photo-attach-btn">
                        <Icon name="image" size={14} style={{marginRight:4}} />Gallery
                        <input type="file" accept="image/*" onChange={e => setEditObsPhotoFile(e.target.files?.[0] || null)} style={{ display: 'none' }} />
                      </label>
	                      {editObsPhotoFile && (
	                        <div className="photo-attach-preview">
	                          <img src={URL.createObjectURL(editObsPhotoFile)} alt="Preview" className="photo-attach-thumb" />
	                          <button type="button" className="btn-icon" onClick={() => { setEditObsPhotoFile(null); setEditObsPhotoPart(''); }} title="Remove"><Icon name="x" size={14} /></button>
	                        </div>
	                      )}
	                      </div>
	                    </div>
	                  </div>

                  {pendingConfirm?.type === 'parentEdit' && pendingConfirm.obsId === editingObsId && (
                    <div className="inline-confirm" style={{ marginTop: '0.5rem' }}>
                      <span className="inline-confirm-msg">{pendingConfirm.message}</span>
                      <div className="inline-confirm-actions">
                        <button className="inline-confirm-no" onClick={cancelConfirm}>Cancel</button>
                        <button className="inline-confirm-yes" style={{ background: 'var(--navy)' }} onClick={() => submitObsPayload(pendingConfirm.obsId, pendingConfirm.payload)}>Save anyway</button>
                      </div>
                    </div>
                  )}
                  <div className="form-actions" style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                    <div></div>
                    <div className="form-actions-right">
                      <button className="btn btn-secondary btn-sm" onClick={() => setEditingObsId(null)}>Cancel</button>
                      <button className="btn btn-primary btn-sm" onClick={saveEditObs}>Save</button>
                    </div>
                  </div>
                </div>
              ) : (
                <div key={obs.id} className="timeline-item">
                  {pendingConfirm?.type === 'obs' && pendingConfirm.id === obs.id ? (
                    <div className="inline-confirm">
                      <span className="inline-confirm-msg">Delete this observation?</span>
                      <div className="inline-confirm-actions">
                        <button className="inline-confirm-no" onClick={cancelConfirm}>Cancel</button>
                        <button className="inline-confirm-yes" onClick={() => confirmDeleteObs(obs.id)}>Delete</button>
                      </div>
                    </div>
                  ) : (
                  <div className="timeline-header">
                    <span className={`event-badge event-${obs.event_type}`}>
                      {obs.event_type.replace('_', ' ')}
                    </span>
                    <div className="timeline-header-right">
                      <span className="timeline-date">{formatDate(obs.date)}</span>
                      <button className="btn-icon btn-icon-save" onClick={() => startEditObs(obs)} title="Edit observation" aria-label="Edit observation"><Icon name="pencil" size={13} /></button>
                      <button className="btn-icon" onClick={() => handleDeleteObs(obs.id)} title="Delete observation" aria-label="Delete observation"><Icon name="trash" size={14} /></button>
                    </div>
                  </div>
                  )}

                  {obs.created_at && (
                    <p className="timeline-logged-meta">Logged {formatLoggedTime(obs.created_at)}</p>
                  )}

                  {obs.repot && (
                    <div className="timeline-details">
                      {obs.repot.new_pot_size && <span>Pot: {obs.repot.new_pot_size}"</span>}
                      {obs.repot.pot_type && <span>Type: {obs.repot.pot_type}</span>}
                      {obs.repot.new_substrate && <span>Substrate: {formatSubstrate(obs.repot.new_substrate)}</span>}
                      {obs.repot.root_condition && <span>Roots: {obs.repot.root_condition}</span>}
                    </div>
                  )}
                  {obs.fertilize && (
                    <div className="timeline-details">
                      {obs.fertilize.product && <span>Product: {obs.fertilize.product}</span>}
                      {obs.fertilize.concentration && <span>{obs.fertilize.concentration}</span>}
                      {obs.fertilize.method && <span>Method: {obs.fertilize.method}</span>}
                    </div>
                  )}
                  {obs.treatment && (
                    <div className="timeline-details">
                      {obs.treatment.treatment_category && <span>Type: {obs.treatment.treatment_category}</span>}
                      {obs.treatment.target && <span>Target: {obs.treatment.target}</span>}
                      {obs.treatment.product && <span>Product: {obs.treatment.product}</span>}
                    </div>
                  )}
                  {obs.propagation && (
                    <div className="timeline-details">
                      <span>
                        {categoryFromPropagation(obs.propagation) === 'seed' ? 'Seed' : 'Clone'}
                        {': '}
                        {PROPAGATION_METHOD_LABELS[obs.propagation.method] || obs.propagation.method}
                      </span>
                      {obs.child_plant_id ? (
                        // State B: Direct child exists
                        <Link
                          to={`/plants/${obs.child_plant_id}`}
                          className="btn btn-secondary btn-sm"
                          style={{ marginLeft: '0.5rem' }}
                        >
                          View child ({obs.child_specimen_id}) <Icon name="arrowRight" size={13} style={{marginLeft:2}} />
                        </Link>
                      ) : obs.propagation_items_count > 0 ? (
                        // State C: Batch items exist
                        <span style={{ marginLeft: '0.5rem', fontSize: '0.82rem', color: 'var(--text-light, #666)' }}>
                          {obs.propagation_items_active_count} active
                          {obs.propagation_items_count - obs.propagation_items_active_count > 0
                            ? ` · ${obs.propagation_items_count - obs.propagation_items_active_count} done`
                            : ''}
                          {' · '}
                          <Link to={`/propagation?detail=${obs.propagation_detail_id}`} style={{ color: 'var(--green, #3a7d44)' }}>Manage batch</Link>
                        </span>
                      ) : (
                        // State A: No child, no batch items — show both CTAs
                        <span style={{ display: 'inline-flex', gap: '0.4rem', marginLeft: '0.5rem' }}>
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => childPanelObsId === obs.id ? setChildPanelObsId(null) : openChildPanel(obs)}
                          >
                            {childPanelObsId === obs.id ? 'Cancel' : '+ Child plant'}
                          </button>
                          {categoryFromPropagation(obs.propagation) !== 'seed' && obs.propagation_detail_id && (
                            <Link
                              to={`/propagation?new=${obs.propagation_detail_id}`}
                              className="btn btn-secondary btn-sm"
                            >
                              + Batch
                            </Link>
                          )}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Inline create-child panel */}
                  {obs.event_type === 'propagation' && childPanelObsId === obs.id && (
                    <div style={{ marginTop: '0.5rem', padding: '0.75rem', background: 'var(--surface-recessed, #f5f5f5)', borderRadius: 8 }}>
                      <div className="form-grid">
                        <div className="form-field">
                          <label className="form-label">Child Status</label>
                          <select
                            value={childPanelForm.child_status}
                            onChange={e => setChildPanelForm(p => ({ ...p, child_status: e.target.value }))}
                            className="form-input"
                          >
                            {statusMetadata.filter(status => status.include_in_collection).map(status => (
                              <option key={status.key} value={status.key}>{formatStatus(status.key, statusByKey)}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <label className="checkbox-label" style={{ marginTop: '0.5rem' }}>
                        <input
                          type="checkbox"
                          checked={childPanelForm.override_name}
                          onChange={e => setChildPanelForm(p => ({ ...p, override_name: e.target.checked }))}
                        />
                        Override child plant name
                      </label>

                      {childPanelForm.override_name && (
                        <div className="form-grid" style={{ marginTop: '0.5rem' }}>
                          <div className="form-field">
                            <label className="form-label">Genus</label>
                            <input
                              type="text"
                              value={childPanelForm.child_genus}
                              onChange={e => setChildPanelForm(p => ({ ...p, child_genus: e.target.value }))}
                              className="form-input"
                              placeholder={plant?.genus}
                            />
                          </div>
                          <div className="form-field">
                            <label className="form-label">Species</label>
                            <input
                              type="text"
                              value={childPanelForm.child_species}
                              onChange={e => setChildPanelForm(p => ({ ...p, child_species: e.target.value }))}
                              className="form-input"
                              placeholder={plant?.species}
                            />
                          </div>
                          <div className="form-field">
                            <label className="form-label">Common Name</label>
                            <input
                              type="text"
                              value={childPanelForm.child_common_name}
                              onChange={e => setChildPanelForm(p => ({ ...p, child_common_name: e.target.value }))}
                              className="form-input"
                              placeholder="Leave blank to omit"
                            />
                          </div>
                        </div>
                      )}

                      {childPanelError && (
                        <p style={{ color: 'var(--danger)', fontSize: '0.85rem', marginTop: '0.5rem' }}>{childPanelError}</p>
                      )}

                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.75rem' }}>
                        <button className="btn btn-secondary btn-sm" onClick={() => setChildPanelObsId(null)}>Cancel</button>
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleCreateChild(obs)}
                          disabled={childPanelSaving}
                        >
                          {childPanelSaving ? 'Creating…' : 'Create Child Plant'}
                        </button>
                      </div>
                    </div>
                  )}
                  {obs.substrate_change && (
                    <div className="timeline-details">
                      {obs.substrate_change.old_substrate && <span>From: {formatSubstrate(obs.substrate_change.old_substrate)}</span>}
                      {obs.substrate_change.new_substrate && <span>To: {formatSubstrate(obs.substrate_change.new_substrate)}</span>}
                      {obs.substrate_change.reason && <span>Reason: {obs.substrate_change.reason}</span>}
                    </div>
                  )}
                  {obs.acclimation && (
                    <div className="timeline-details">
                      {obs.acclimation.method && <span>Method: {obs.acclimation.method}</span>}
                      {obs.acclimation.source_condition && <span>From: {obs.acclimation.source_condition}</span>}
                    </div>
                  )}

                  {obs.notes && <p className="timeline-notes">{obs.notes}</p>}

                  {/* Linked photos */}
                  {photos.filter(p => p.observation_id === obs.id).length > 0 && (
                    <div className="timeline-photos">
                      {photos.filter(p => p.observation_id === obs.id).map(p => (
                        <img
                          key={p.id}
                          src={photoUrl(p.id, p.rotation_version, p.created_at)}
                          alt=""
                          className="timeline-photo-thumb"
                          onClick={() => setLightboxPhoto(p)}
                          loading="lazy"
                        />
                      ))}
                    </div>
                  )}

                  {/* Inline edit history */}
                  {obs.has_history && (
                    <div className="inline-history">
                      <button className="inline-history-toggle" onClick={() => toggleObsHistory(obs.id)}>
                        <Icon name={obsHistories[obs.id]?.open ? 'chevronDown' : 'chevronRight'} size={11} /> Edited
                      </button>
                      {obsHistories[obs.id]?.open && (
                        <div className="history-list history-list--inline">
                          {obsHistories[obs.id]?.items === null && <p className="fo-empty">Loading…</p>}
                          {obsHistories[obs.id]?.items?.length === 0 && <p className="fo-empty">No field-level history recorded.</p>}
                          {obsHistories[obs.id]?.items?.map(h => (
                            <div key={h.id} className="history-item">
                              <div className="history-change">
                                <span className="history-field">{h.field_name}</span>
                                {h.old_value && <span className="history-old">{h.old_value}</span>}
                                <span className="history-arrow"><Icon name="arrowRight" size={13} /></span>
                                <span className="history-new">{h.new_value || 'empty'}</span>
                              </div>
                              <span className="history-date">
                                {formatDateTime(h.changed_at)}{h.actor ? ` · ${h.actor.display_name || h.actor.email}` : ''}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            ))}
          </div>
        )}
        {!observationsExpanded && hiddenObservationCount > 0 && (
          <button className="activity-section-more" onClick={() => setObservationsOpen(true)}>
            Show {hiddenObservationCount} more observation{hiddenObservationCount !== 1 ? 's' : ''}
          </button>
        )}
      </div>

      {/* -- Measurements -------------------------------------------------- */}
      <div ref={measFormRef} className="detail-section" style={{ ...(isWishlistPlant ? { display: 'none' } : {}), scrollMarginTop: 'calc(env(safe-area-inset-top, 0px) + 1rem)' }}>
        <div className="section-header-row">
          <button
            type="button"
            className="section-toggle-btn"
            onClick={() => setMeasurementsOpen(open => !open)}
            aria-expanded={measurementsExpanded}
          >
            Measurements ({measurements.length})
            <span className="collapse-icon">
              <Icon name={measurementsExpanded ? 'chevronDown' : 'chevronRight'} size={14} />
            </span>
          </button>
          {canAddCareRecords && (
            <button type="button" className="btn btn-ghost btn-sm section-header-add-btn" onClick={openMeasForm}>
              <Icon name="plus" size={13} /> Add
            </button>
          )}
        </div>

        {/* -- Measurement Form -------------------------------------------- */}
        {canAddCareRecords && measFormOpen && (
          <div style={{ position: 'relative' }}>
            <form onSubmit={handleMeasSubmit} className="obs-form">
              {measError && <p className="error">{measError}</p>}

            <div className="form-grid">
              <div className="form-field">
                <label className="form-label">Date</label>
                <input type="date" name="date" value={measForm.date} onChange={handleMeasChange} className="form-input" />
              </div>
              <div className="form-field">
                <label className="form-label">Height (inches)</label>
                <input type="number" name="height_inches" value={measForm.height_inches} onChange={handleMeasChange} className="form-input" step="0.25" min="0" inputMode="decimal" placeholder="0.0" />
              </div>
              <div className="form-field">
                <label className="form-label">Longest Leaf (inches)</label>
                <input type="number" name="longest_leaf_inches" value={measForm.longest_leaf_inches} onChange={handleMeasChange} className="form-input" step="0.25" min="0" inputMode="decimal" placeholder="0.0" />
              </div>
              <div className="form-field">
                <label className="form-label">Leaf Count</label>
                <input type="number" name="leaf_count" value={measForm.leaf_count} onChange={handleMeasChange} className="form-input" min="0" inputMode="numeric" placeholder="0" />
              </div>
            </div>

            <div className="form-field" style={{ marginTop: '0.75rem' }}>
              <label className="form-label">Notes</label>
              <textarea name="notes" value={measForm.notes} onChange={handleMeasChange} className="form-input form-textarea" rows="2" placeholder="Optional notes..." />
            </div>

            {/* Photo attachment */}
            <div className="form-field" style={{ marginTop: '0.75rem' }}>
              <label className="form-label">Attach Photo</label>
              <div className="photo-upload-controls">
                <PhotoLabelField value={measPhotoPart} onChange={setMeasPhotoPart} />
                <div className="photo-attach-row">
                <label className="btn btn-secondary btn-sm photo-attach-btn">
                  <Icon name="camera" size={14} style={{marginRight:4}} />Camera
                  <input type="file" accept="image/*" capture="environment" onChange={e => setMeasPhotoFile(e.target.files?.[0] || null)} style={{ display: 'none' }} />
                </label>
                <label className="btn btn-secondary btn-sm photo-attach-btn">
                  <Icon name="image" size={14} style={{marginRight:4}} />Gallery
                  <input type="file" accept="image/*" onChange={e => setMeasPhotoFile(e.target.files?.[0] || null)} style={{ display: 'none' }} />
                </label>
                {measPhotoFile && (
                  <div className="photo-attach-preview">
                    <img src={URL.createObjectURL(measPhotoFile)} alt="Preview" className="photo-attach-thumb" />
                    <button type="button" className="btn-icon" onClick={() => { setMeasPhotoFile(null); setMeasPhotoPart(''); }} title="Remove"><Icon name="x" size={14} /></button>
                  </div>
                )}
                </div>
              </div>
            </div>

            <div className="form-actions" style={{ marginTop: '0.75rem', marginBottom: 0 }}>
              <div></div>
              <div className="form-actions-right">
                <button type="button" className="btn btn-secondary btn-sm" onClick={resetMeasForm}>Cancel</button>
                <button type="submit" className="btn btn-primary btn-sm" disabled={measSaving}>
                  {measSaving ? 'Saving...' : 'Log Measurement'}
                </button>
              </div>
            </div>
            </form>
            <LoadingOverlay
              isLoading={measSaving}
              message="Saving measurement..."
            />
          </div>
        )}

        {/* -- Measurement Table ------------------------------------------- */}
        {measurements.length === 0 && !measFormOpen ? (
          <p className="empty-state">No measurements yet</p>
        ) : measurements.length > 0 ? (
          <table className="measurement-table" style={{ marginTop: measFormOpen ? '1rem' : 0 }}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Height</th>
                <th>Longest Leaf</th>
                <th>Leaf Count</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {visibleMeasurements.map(m => (
                editingMeasId === m.id ? (
                  <tr key={m.id} className="editing-row">
                    <td><input type="date" name="date" value={editMeasForm.date} onChange={handleEditMeasChange} className="form-input form-input-sm" /></td>
                    <td><input type="number" name="height_inches" value={editMeasForm.height_inches} onChange={handleEditMeasChange} className="form-input form-input-sm" step="0.25" min="0" inputMode="decimal" /></td>
                    <td><input type="number" name="longest_leaf_inches" value={editMeasForm.longest_leaf_inches} onChange={handleEditMeasChange} className="form-input form-input-sm" step="0.25" min="0" inputMode="decimal" /></td>
                    <td><input type="number" name="leaf_count" value={editMeasForm.leaf_count} onChange={handleEditMeasChange} className="form-input form-input-sm" min="0" inputMode="numeric" /></td>
                    <td className="row-actions">
                      <button className="btn-icon btn-icon-save" onClick={saveEditMeas} title="Save measurement" aria-label="Save measurement"><Icon name="check" size={14} /></button>
                      <button className="btn-icon" onClick={() => { setEditingMeasId(null); setEditMeasPhotoFile(null); }} title="Cancel edit" aria-label="Cancel edit"><Icon name="x" size={14} /></button>
                    </td>
                  </tr>
                ) : (
                  <React.Fragment key={m.id}>
                    {pendingConfirm?.type === 'meas' && pendingConfirm.id === m.id ? (
                      <tr>
                        <td colSpan="5">
                          <div className="inline-confirm">
                            <span className="inline-confirm-msg">Delete this measurement?</span>
                            <div className="inline-confirm-actions">
                              <button className="inline-confirm-no" onClick={cancelConfirm}>Cancel</button>
                              <button className="inline-confirm-yes" onClick={() => confirmDeleteMeas(m.id)}>Delete</button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ) : (
                    <tr>
                      <td>{formatDate(m.date)}</td>
                      <td>{m.height_inches ? `${m.height_inches}"` : '—'}</td>
                      <td>{m.longest_leaf_inches ? `${m.longest_leaf_inches}"` : '—'}</td>
                      <td>{m.leaf_count ?? '—'}</td>
                      <td className="row-actions">
                        <button className="btn-icon btn-icon-save" onClick={() => startEditMeas(m)} title="Edit measurement" aria-label="Edit measurement"><Icon name="pencil" size={13} /></button>
                        <button className="btn-icon" onClick={() => handleDeleteMeas(m.id)} title="Delete measurement" aria-label="Delete measurement"><Icon name="trash" size={14} /></button>
                      </td>
                    </tr>
                    )}
                    {m.created_at && (
                      <tr>
                        <td colSpan="5" className="measurement-logged-meta">
                          Logged {formatLoggedTime(m.created_at)}
                        </td>
                      </tr>
                    )}
                    {photos.filter(p => p.measurement_id === m.id).length > 0 && (
                      <tr>
                        <td colSpan="5" className="meas-photo-cell">
                          {photos.filter(p => p.measurement_id === m.id).map(p => (
                            <img
                              key={p.id}
                              src={photoUrl(p.id, p.rotation_version, p.created_at)}
                              alt=""
                              className="timeline-photo-thumb"
                              onClick={() => setLightboxPhoto(p)}
                              loading="lazy"
                            />
                          ))}
                        </td>
                      </tr>
                    )}
                    {m.notes && (
                      <tr>
                        <td colSpan="5" className="timeline-notes" style={{ paddingTop: 0, paddingBottom: '0.5rem' }}>
                          {m.notes}
                        </td>
                      </tr>
                    )}
                    {m.has_history && (
                      <tr>
                        <td colSpan="5" style={{ paddingTop: 0, paddingBottom: '0.35rem' }}>
                          <div className="inline-history">
                            <button className="inline-history-toggle" onClick={() => toggleMeasHistory(m.id)}>
                              <Icon name={measHistories[m.id]?.open ? 'chevronDown' : 'chevronRight'} size={11} /> Edited
                            </button>
                            {measHistories[m.id]?.open && (
                              <div className="history-list history-list--inline">
                                {measHistories[m.id]?.items === null && <p className="fo-empty">Loading…</p>}
                                {measHistories[m.id]?.items?.length === 0 && <p className="fo-empty">No field-level history recorded.</p>}
                                {measHistories[m.id]?.items?.map(h => (
                                  <div key={h.id} className="history-item">
                                    <div className="history-change">
                                      <span className="history-field">{h.field_name}</span>
                                      {h.old_value && <span className="history-old">{h.old_value}</span>}
                                      <span className="history-arrow"><Icon name="arrowRight" size={13} /></span>
                                      <span className="history-new">{h.new_value || 'empty'}</span>
                                    </div>
                                    <span className="history-date">
                                      {formatDateTime(h.changed_at)}{h.actor ? ` · ${h.actor.display_name || h.actor.email}` : ''}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              ))}
            </tbody>
          </table>
        ) : null}
        {!measurementsExpanded && hiddenMeasurementCount > 0 && (
          <button className="activity-section-more" onClick={() => setMeasurementsOpen(true)}>
            Show {hiddenMeasurementCount} more measurement{hiddenMeasurementCount !== 1 ? 's' : ''}
          </button>
        )}
      </div>

      {/* -- Photo Timeline ------------------------------------------------- */}
      {photos.length > 0 && (
        <div className="detail-section">
          <div className="section-header-row">
            <button
              type="button"
              className="section-toggle-btn"
              onClick={() => setPhotosOpen(!photosOpen)}
              aria-expanded={photosOpen}
            >
              Photos ({photos.length})
              <span className="collapse-icon">
                <Icon name={photosOpen ? 'chevronDown' : 'chevronRight'} size={14} />
              </span>
            </button>
          </div>
          {photosOpen && (
            <div style={{ position: 'relative' }}>
              <div className="photo-grid">
                {photos.map(photo => (
                  <div key={photo.id} className="photo-grid-item" onClick={() => setLightboxPhoto(photo)}>
                    <img
                      src={photoUrl(photo.id, photo.rotation_version, photo.created_at)}
                      alt={photo.caption || 'Plant photo'}
                      className="photo-grid-img"
                      loading="lazy"
                    />
                    {photo.photo_part && (
                      <span className="photo-grid-part">{photoPartLabel(photo.photo_part)}</span>
                    )}
                    {photo.growth_stage && (
                      <span className="photo-grid-stage">{photoGrowthStageLabel(photo.growth_stage)}</span>
                    )}
                    <span className="photo-grid-date">{photo.taken_at ? formatDate(photo.taken_at.split('T')[0]) : ''}</span>
                  </div>
                ))}
              </div>
              <LoadingOverlay
                isLoading={uploading}
                message="Uploading..."
              />
            </div>
          )}
        </div>
      )}

      {/* -- Photo Lightbox -------------------------------------------------- */}
      {lightboxPhoto && createPortal((
        <div className="lightbox-overlay" onClick={() => { setLightboxPhoto(null); setMultiIdResults(null); setMultiIdError(null); setSelectedPhotoIds(new Set()); setMultiIdConfirm(null); }}>
          <div className="lightbox-content" onClick={e => e.stopPropagation()}>
            <div style={{ position: 'relative' }}>
              <img
                src={photoUrl(lightboxPhoto.id, lightboxPhoto.rotation_version, lightboxPhoto.created_at)}
                alt={lightboxPhoto.caption || 'Plant photo'}
                className="lightbox-img"
              />
              <LoadingOverlay
                isLoading={multiIdLoading}
                message="Identifying..."
              />
            </div>

            {/* Action bar */}
            <div className="lightbox-bar">
              <span className="lightbox-date">{lightboxPhoto.taken_at ? formatDate(lightboxPhoto.taken_at.split('T')[0]) : ''}</span>

              <details className="lightbox-metadata">
                <summary>
                  Metadata
                  {(lightboxPhoto.photo_part || lightboxPhoto.growth_stage) && (
                    <span>
                      {[photoPartLabel(lightboxPhoto.photo_part), photoGrowthStageLabel(lightboxPhoto.growth_stage)]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  )}
                </summary>
                <div className="lightbox-metadata-panel">
                  <LightboxSelectField label="Photo label">
                    <PhotoPartSelect
                      value={lightboxPhoto.photo_part || ''}
                      onChange={value => handlePhotoPartChange(lightboxPhoto.id, value)}
                      className="lightbox-select"
                    />
                  </LightboxSelectField>
                  <LightboxSelectField label="Foliage stage">
                    <PhotoGrowthStageSelect
                      value={lightboxPhoto.growth_stage || ''}
                      onChange={value => handlePhotoGrowthStageChange(lightboxPhoto.id, value)}
                      className="lightbox-select"
                    />
                  </LightboxSelectField>
                </div>
              </details>

              {/* Only show "Set as Cover" when there are multiple photos and this
                  isn't already the effective cover (default_photo_id set, or it's
                  the first photo with no default set) */}
              {photos.length > 1 && !(
                (plant.default_photo_id == null && lightboxPhoto.id === heroPhoto?.id) ||
                plant.default_photo_id === lightboxPhoto.id
              ) && (
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => handleSetCoverPhoto(lightboxPhoto.id)}
                  title="Set as cover photo"
                >
                  ☆ Set as Cover
                </button>
              )}
              {plant.default_photo_id === lightboxPhoto.id && (
                <button
                  className="btn btn-secondary btn-sm"
                  style={{ borderColor: 'var(--green)', color: 'var(--green)' }}
                  onClick={() => handleSetCoverPhoto(lightboxPhoto.id)}
                  title="Currently set as cover — click to clear"
                >
                  ★ Cover Photo
                </button>
              )}
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => handleRotatePhoto(lightboxPhoto.id)}
                title="Rotate 90° clockwise"
              >
                <Icon name="rotateCw" size={14} /> Rotate
              </button>
              {lightboxPhoto.reference_consent ? (
                <LightboxSelectField label={
                  lightboxPhoto.reference_status === 'approved' ? 'Approved' :
                  lightboxPhoto.reference_status === 'rejected' ? 'Not approved' :
                  'Pending review'
                }>
                  <button
                    className="btn btn-secondary btn-sm"
                    style={{ borderColor: 'var(--green)', color: 'var(--green)' }}
                    onClick={() => handleReferenceConsentToggle(lightboxPhoto.id, lightboxPhoto.reference_consent)}
                    title={!lightboxPhoto.photo_part ? 'Tip: adding a photo label helps reviewers' : 'Click to withdraw sharing'}
                  >
                    ★ Shared
                  </button>
                </LightboxSelectField>
              ) : (
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => handleReferenceConsentToggle(lightboxPhoto.id, lightboxPhoto.reference_consent)}
                >
                  ☆ Share as Reference
                </button>
              )}
              {pendingConfirm?.type === 'photo' && pendingConfirm.id === lightboxPhoto.id ? (
                <div className="inline-confirm">
                  <span className="inline-confirm-msg">Delete this photo?</span>
                  <div className="inline-confirm-actions">
                    <button className="inline-confirm-no" onClick={cancelConfirm}>Cancel</button>
                    <button className="inline-confirm-yes" onClick={() => confirmDeletePhoto(lightboxPhoto.id)}>Delete</button>
                  </div>
                </div>
              ) : (
                <button
                  className="btn btn-danger btn-sm"
                  onClick={() => handleDeletePhoto(lightboxPhoto.id)}
                >
                  Delete
                </button>
              )}
            </div>

            {/* Multi-photo identification panel */}
            <div className="lightbox-id-tools">
              <div className="lightbox-id-tools-label">Identify plant</div>
              {!multiIdResults && (
                <>
                  <p className="lightbox-id-hint">Select photos to identify ({selectedPhotoIds.size}/5)</p>
                  <div className="id-multi-photo-selector">
                    {photos.map(p => {
                      const sel = selectedPhotoIds.has(p.id);
                      return (
                        <button
                          key={p.id}
                          type="button"
                          className={`id-multi-photo-thumb-btn${sel ? ' selected' : ''}`}
                          onClick={() => {
                            setSelectedPhotoIds(prev => {
                              const next = new Set(prev);
                              if (sel) { next.delete(p.id); } else if (next.size < 5) { next.add(p.id); }
                              return next;
                            });
                          }}
                          aria-label={sel ? 'Deselect photo' : 'Select photo for ID'}
                          title={sel ? 'Deselect' : 'Select for identification'}
                        >
                          <img src={photoUrl(p.id, p.rotation_version, p.created_at)} alt="" />
                          {sel && <span className="id-multi-check"><Icon name="check" size={10} /></span>}
                        </button>
                      );
                    })}
                  </div>
                  {multiIdError && <p className="error" style={{ marginTop: '0.4rem', fontSize: '0.82rem' }}>{multiIdError}</p>}
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={handleMultiIdentify}
                    disabled={selectedPhotoIds.size === 0 || multiIdLoading}
                    style={{ marginTop: '0.5rem' }}
                  >
                    {multiIdLoading
                      ? <><Icon name="clock" size={14} style={{ marginRight: 4 }} />Identifying...</>
                      : <><Icon name="search" size={14} style={{ marginRight: 4 }} />Identify plant</>}
                  </button>
                </>
              )}

              {multiIdResults && (
                <div className="id-results-panel">
                  <IdentificationResults
                    suggestions={multiIdResults.top_suggestions}
                    refImages={[]}
                    quotaInfo={multiIdQuotaInfo}
                    provider={multiIdQuotaInfo?.provider || 'plantnet'}
                    remainingRequests={multiIdResults.remaining_requests}
                    onAccept={handleAcceptMultiSuggestion}
                    plantId={id}
                    onConfirm={(updatedPlant) => {
                      setPlant(updatedPlant);
                      fetchData();
                      setMultiIdResults(null);
                      setSelectedPhotoIds(new Set());
                      setToast('Identification confirmed');
                      setTimeout(() => setToast(null), 3000);
                    }}
                    onDismiss={() => { setMultiIdResults(null); setSelectedPhotoIds(new Set()); }}
                    dismissLabel="Dismiss"
                  />
                </div>
              )}
            </div>

            {/* Accept confirmation modal */}
            {multiIdConfirm && createPortal((
              <div className="modal-overlay" onClick={() => setMultiIdConfirm(null)}>
                <div className="modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
                  <div className="modal-header">
                    <h2>Accept identification?</h2>
                  </div>
                  {(() => {
                    const s = multiIdConfirm.suggestion;
                    const speciesEpithet = (s.species || '').replace(s.genus || '', '').trim();
                    const changes = [];
                    if (s.genus && s.genus !== plant.genus) changes.push({ label: 'Genus', value: s.genus });
                    if (speciesEpithet && speciesEpithet !== plant.species) changes.push({ label: 'Species', value: speciesEpithet });
                    if (s.common_names?.[0] && s.common_names[0] !== plant.common_name) changes.push({ label: 'Common name', value: s.common_names[0] });
                    return (
                      <>
                        <p style={{ margin: '0 0 0.75rem', fontSize: '0.9rem', color: 'var(--text-muted)' }}>
                          <em>{s.species}</em>
                          {s.common_names?.[0] ? ` — ${s.common_names[0]}` : ''}
                        </p>
                        {changes.length > 0 ? (
                          <ul style={{ margin: '0 0 1rem', padding: '0 0 0 1.2rem', fontSize: '0.88rem' }}>
                            {changes.map(c => (
                              <li key={c.label}><strong>{c.label}:</strong> {c.value}</li>
                            ))}
                          </ul>
                        ) : (
                          <p style={{ margin: '0 0 1rem', fontSize: '0.88rem', color: 'var(--text-muted)' }}>No fields will change.</p>
                        )}
                      </>
                    );
                  })()}
                  <div className="modal-actions">
                    <button className="btn btn-secondary" onClick={() => setMultiIdConfirm(null)}>Cancel</button>
                    <button className="btn btn-primary" onClick={handleConfirmAccept}>Confirm</button>
                  </div>
                </div>
              </div>
            ), document.body)}

            <button className="lightbox-close" onClick={() => { setLightboxPhoto(null); setMultiIdResults(null); setMultiIdError(null); setSelectedPhotoIds(new Set()); setMultiIdConfirm(null); }}><Icon name="x" size={18} /></button>

            {/* Prev/Next navigation */}
            {photos.length > 1 && (() => {
              const idx = photos.findIndex(p => p.id === lightboxPhoto.id);
              return (
                <>
                  {idx > 0 && (
                    <button className="lightbox-nav lightbox-prev" onClick={() => { setLightboxPhoto(photos[idx - 1]); setMultiIdResults(null); setMultiIdError(null); }}><Icon name="chevronLeft" size={20} /></button>
                  )}
                  {idx < photos.length - 1 && (
                    <button className="lightbox-nav lightbox-next" onClick={() => { setLightboxPhoto(photos[idx + 1]); setMultiIdResults(null); setMultiIdError(null); }}><Icon name="chevronRight" size={20} /></button>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      ), document.body)}

      {/* -- Lineage -------------------------------------------------------- */}
      <div className="detail-section" style={isWishlistPlant ? { display: 'none' } : undefined}>
        <div className="section-header-row">
          <button
            type="button"
            className="section-toggle-btn"
            onClick={handleToggleLineage}
            aria-expanded={lineageOpen}
          >
            Lineage
            <span className="collapse-icon">
              <Icon name={lineageOpen ? 'chevronDown' : 'chevronRight'} size={14} />
            </span>
          </button>
        </div>
        {lineageOpen && (
          <div style={{ padding: '0.25rem 0' }}>
            {lineageLoading && <p style={{ color: 'var(--text-light, #888)', fontSize: '0.85rem' }}>Loading…</p>}
            {lineageError && <p style={{ color: 'var(--danger)', fontSize: '0.85rem' }}>{lineageError}</p>}
            {lineage && !lineageLoading && (
              lineage.length > 0
                ? lineage.map((root, i) => (
                    <div key={root.id} style={{ marginBottom: lineage.length > 1 && i < lineage.length - 1 ? '1rem' : 0 }}>
                      {lineage.length > 1 && (
                        <p style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-light, #888)', marginBottom: '0.35rem' }}>
                          {i === 0 ? 'Seed Parent Lineage' : 'Pollen Donor Lineage'}
                        </p>
                      )}
                      <TreeNode node={root} />
                    </div>
                  ))
                : <p style={{ color: 'var(--text-light, #888)', fontSize: '0.85rem' }}>No lineage data found.</p>
            )}
          </div>
        )}
      </div>

      {/* -- Edit History -------------------------------------------------- */}
      {visibleHistory.length > 0 && (
        <div className="detail-section">
          <div className="section-header-row">
            <button
              type="button"
              className="section-toggle-btn"
              onClick={() => setHistoryOpen(!historyOpen)}
              aria-expanded={historyOpen}
            >
              Edit History ({visibleHistory.length})
              <span className="collapse-icon">
                <Icon name={historyOpen ? 'chevronDown' : 'chevronRight'} size={14} />
              </span>
            </button>
          </div>
          {historyOpen && (
            <div className="history-list">
              {visibleHistory.map(h => (
                <div key={h.id} className="history-item">
                  <div className="history-change">
                    <span className="history-field">{h.field_name}</span>
                    {h.old_value && (
                      <span className="history-old">{h.old_value}</span>
                    )}
                    <span className="history-arrow"><Icon name="arrowRight" size={13} /></span>
                    <span className="history-new">{h.new_value || 'empty'}</span>
                  </div>
                  <span className="history-date">
                    {formatDateTime(h.changed_at)}
                    {h.actor ? ` · ${h.actor.display_name || h.actor.email}` : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default PlantDetail;
